import React, { useEffect, useState, useCallback, useRef, useMemo } from "react"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import { getSessionItem, getLocalItem } from "../../utils/storage"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Spinner,
} from "reactstrap"
import GuestRestrictedPlaceholder from "../../components/Common/GuestRestrictedPlaceholder"
import SkeletonParagraph from "../../additional_components/common/SkeletonParagraph"
import SEO from "../../components/Common/SEO"
import BackButton from "../../components/Common/BackButton"
import { generateSlug, getAppDetailsLink, generateKeywords, generateOGImage, truncateText } from "../../utils/seoUtils"
import { SITE_CONFIG } from "../../config/seoConfig"
import NotFound from "../../components/Common/NotFound"
import axios from "axios"
import { api } from "../../utils/variables"
import { useParams, Link, useLocation, useNavigate } from "react-router-dom"
import Reviews from "../../additional_components/common/dashboardComponent/Reviews"
import TrackingDetailsPopup from "../../additional_components/common/Modals/TrackingDetails"
import ChatbotUI from "../../additional_components/common/Modals/Chat"
import ReactApexChart from "react-apexcharts"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import AIReportSkeleton from "../../additional_components/common/AIReportSkeleton"
import AIReportDisplay from "../../additional_components/common/AIReportDisplay"
import { FaInfoCircle, FaStar, FaChartLine, FaRobot, FaSearch, FaRegListAlt, FaArrowLeft, FaHeart, FaChartPie, FaVideo, FaTrophy } from "react-icons/fa"
import { Info, Check } from "react-feather"
import PlatformIcon from "../../components/Common/PlatformIcon"
import PlatformsApi from "../../services/platformsApi"
import googlelogo from "../../assets/images/App logo 68.png"
import dwnload from "../../assets/images/material-symbols_download.png"
import growth from "../../assets/images/growth 1.png"
import mans from "../../assets/images/mans.png"
import Forecasting from "./Forecasting"
import AppScanner from "../AppScanner/AppScanner"
import SocialMediaComparison from "../SocialMediaComparison/SocialMediaComparison"
import solar from "../../assets/images/solar_ranking-bold.png"
import rocket from "../../assets/images/rocket.png"
import descrip from "../../assets/images/ph_note-fill.png"
import screenshot from "../../assets/images/screenshot.png"
import g1 from "../../assets/images/g1.png"
import { navigateToLogin } from "../../utils/loginRedirect"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"

import { Line } from "react-chartjs-2"
import appTrackingPlanModal from "../../components/Common/Modal/AppsTrackingPlanModal"
import AppsTrackingPlanModal from "../../components/Common/Modal/AppsTrackingPlanModal"
import { ai_api } from "../../utils/variables"

// Motion and animation imports
import { motion, useMotionValue, useTransform, animate, useInView } from "framer-motion"
import { TrendingUp, TrendingDown, ChevronDown, Download, Star, DollarSign, BarChart3, Zap, ThumbsUp, ThumbsDown, Send, Activity, Calendar, Search, MessageSquare, ArrowUpRight, ArrowDownRight, Maximize2 } from "lucide-react"

// Recharts imports for new analytics UI
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { color } from "echarts"


// ── New Analytics UI Components ────────────────────────────────────

const ChartTooltip = ({ active, payload, label, format, fmtValue }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 shadow-xl text-xs" style={{
      backgroundColor: "rgba(23, 24, 33, 0.95)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius: "8px",
      padding: "8px 12px",
      color: "white"
    }}>
      <p className="font-medium mb-1" style={{ color: "rgba(255, 255, 255, 0.6)" }}>{label}</p>
      <span className="font-mono font-semibold">
        {fmtValue(payload[0].value, format)}
      </span>
    </div>
  );
};

const MetricChart = ({ metric, height = 200, fmtValue }) => {
  const chartData = metric.data.map((d) => ({ date: d.date, value: d.value }));
  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`grad-${metric.id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={metric.color} stopOpacity={0.25} />
              <stop offset="100%" stopColor={metric.color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: "rgba(255, 255, 255, 0.4)" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 10, fill: "rgba(255, 255, 255, 0.4)" }} tickLine={false} axisLine={false} width={45} tickFormatter={(v) => fmtValue(v, metric.format)} />
          <RechartsTooltip content={<ChartTooltip format={metric.format} fmtValue={fmtValue} />} />
          <Area type="monotone" dataKey="value" stroke={metric.color} strokeWidth={2} fill={`url(#grad-${metric.id})`} dot={false} activeDot={{ r: 4, fill: metric.color, stroke: "rgba(23, 24, 33, 1)", strokeWidth: 2 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const ZoomableMetricChart = ({ metric, height = 330, fmtValue }) => {
  const chartData = metric.data.map((d) => d.value);
  const categories = metric.data.map((d) => d.date);

  const options = {
    chart: {
      type: "area",
      zoom: {
        enabled: true,
        type: 'x',
        autoScaleYaxis: true
      },
      toolbar: {
        show: false
      },
      background: 'transparent'
    },
    colors: [metric.color || "#F6255A"],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.25,
        opacityTo: 0,
        stops: [0, 100]
      }
    },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 2 },
    xaxis: {
      categories: categories,
      labels: {
        style: { colors: "rgba(255, 255, 255, 0.4)", fontSize: "10px" }
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false }
    },
    yaxis: {
      labels: {
        formatter: (val) => fmtValue ? fmtValue(val, metric.format) : val,
        style: { colors: "rgba(255, 255, 255, 0.4)", fontSize: "10px" }
      }
    },
    grid: {
      borderColor: "rgba(255, 255, 255, 0.1)",
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } }
    },
    tooltip: {
      theme: "dark",
      y: {
        formatter: (val) => fmtValue ? fmtValue(val, metric.format) : val
      }
    }
  };

  const series = [{
    name: metric.title || "Value",
    data: chartData
  }];

  return (
    <div style={{ height }} className="w-full">
      <ReactApexChart options={options} series={series} type="area" height="100%" width="100%" />
    </div>
  );
};

const MetricChartSection = ({ metric, onExpand, fmtValue }) => {
  const Icon = metric.icon || BarChart3;
  const isPositive = metric.pct >= 0;
  const isGood = metric.higherIsBetter ? isPositive : !isPositive;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-20px" }}
      transition={{ duration: 0.4 }}
      style={{ height: '100%' }}
    >
      <div
        onClick={onExpand}
        className="group"
        style={{
          backgroundColor: "rgba(23, 24, 33, 0.6)",
          border: "1px solid rgba(255, 255, 255, 0.05)",
          borderRadius: "12px",
          overflow: "hidden",
          height: "100%",
          transition: "all 0.3s ease",
          cursor: "pointer",
          backdropFilter: "blur(4px)"
        }}
      >
        <div style={{ padding: "16px" }}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center gap-2 overflow-hidden">
              <div style={{
                height: "24px",
                width: "24px",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(246, 37, 90, 0.1)"
              }}>
                <Icon size={14} style={{ color: "#F6255A" }} />
              </div>
              <h3 style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "white",
                margin: 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}>
                {metric.title}
              </h3>
            </div>
            <div className="d-flex align-items-center gap-2 shrink-0">
              <div style={{
                fontSize: "9px",
                fontWeight: "500",
                padding: "2px 6px",
                borderRadius: "12px",
                border: metric.higherIsBetter ? "1px solid rgba(16, 185, 129, 0.3)" : "1px solid rgba(245, 158, 11, 0.3)",
                color: metric.higherIsBetter ? "#10b981" : "#f59e0b",
                display: "flex",
                alignItems: "center",
                gap: "2px"
              }}>
                {metric.higherIsBetter ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
                {metric.higherIsBetter ? "↑ Better" : "↓ Better"}
              </div>
              <div className="opacity-0 group-hover-opacity-100" style={{ transition: "opacity 0.2s", color: "rgba(255, 255, 255, 0.4)" }}>
                <Maximize2 size={14} />
              </div>
            </div>
          </div>

          <div className="d-flex align-items-baseline gap-2 mt-1">
            <p style={{
              fontSize: "20px",
              fontWeight: "bold",
              fontFamily: "monospace",
              color: "white",
              margin: 0
            }}>
              {fmtValue(metric.currentValue, metric.format)}
            </p>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "11px",
              fontWeight: "500",
              color: isGood ? "#10b981" : "#ef4444"
            }}>
              {isPositive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              <span style={{ fontFamily: "monospace" }}>{isPositive ? "+" : ""}{metric.pct.toFixed(1)}%</span>
            </div>
          </div>
        </div>

        <div style={{ padding: "0 8px 8px 8px" }}>
          <MetricChart metric={metric} height={140} fmtValue={fmtValue} />
        </div>
      </div>
    </motion.div>
  );
};

const MetricSummaryRow = ({ metric, onClick, fmtValue }) => {
  const isPositive = metric.pct >= 0;
  const isGood = metric.higherIsBetter ? isPositive : !isPositive;

  return (
    <div
      onClick={onClick}
      className="d-flex align-items-center justify-content-between w-100 group"
      style={{
        padding: "8px 12px",
        borderRadius: "8px",
        cursor: "pointer",
        transition: "all 0.2s ease",
        textAlign: "left"
      }}
      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)"}
      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
    >
      <span style={{
        fontSize: "12px",
        color: "rgba(255, 255, 255, 0.5)",
        transition: "color 0.2s"
      }}>
        {metric.title}
      </span>
      <div className="d-flex align-items-center gap-2">
        <span style={{
          fontSize: "12px",
          fontWeight: "500",
          fontFamily: "monospace",
          color: "white"
        }}>
          {fmtValue(metric.currentValue, metric.format)}
        </span>
        <span style={{
          fontSize: "10px",
          fontWeight: "500",
          fontFamily: "monospace",
          color: isGood ? "#10b981" : "#ef4444"
        }}>
          {isPositive ? "+" : ""}{metric.pct.toFixed(1)}%
        </span>
      </div>
    </div>
  );
};

const ExpandedChartDialog = ({ metric, open, onClose, fmtValue }) => {
  if (!metric) return null;

  return (
    <Modal
      isOpen={open}
      toggle={onClose}
      centered
      size="lg"
      contentClassName="bg-dark border-0 overflow-hidden"
      style={{ maxWidth: "850px" }}
    >
      <div style={{
        backgroundColor: "rgba(23, 24, 33, 1)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        borderRadius: "16px",
        overflow: "hidden"
      }}>
        <div style={{ padding: "24px", borderBottom: "1px solid rgba(255, 255, 255, 0.05)" }} className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <div style={{
              height: "40px",
              width: "40px",
              borderRadius: "10px",
              backgroundColor: "rgba(246, 37, 90, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              {metric.icon && <metric.icon size={20} style={{ color: "#F6255A" }} />}
            </div>
            <div>
              <h2 style={{ fontSize: "18px", fontWeight: "bold", color: "white", margin: 0 }}>{metric.title}</h2>
              <p style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.5)", margin: "2px 0 0 0" }}>{metric.description}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255, 255, 255, 0.05)",
              border: "none",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(255, 255, 255, 0.4)",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = "white"}
            onMouseLeave={(e) => e.currentTarget.style.color = "rgba(255, 255, 255, 0.4)"}
          >
            ×
          </button>
        </div>

        <div style={{ padding: "32px" }}>
          <div className="row mb-5">
            <div className="col-md-4">
              <div style={{ backgroundColor: "rgba(255, 255, 255, 0.02)", padding: "16px", borderRadius: "12px", border: "1px solid rgba(255, 255, 255, 0.05)" }}>
                <p style={{ fontSize: "11px", textTransform: "uppercase", color: "rgba(255, 255, 255, 0.4)", letterSpacing: "0.05em", margin: "0 0 8px 0" }}>Current Value</p>
                <p style={{ fontSize: "28px", fontWeight: "bold", color: "white", margin: 0, fontFamily: "monospace" }}>{fmtValue(metric.currentValue, metric.format)}</p>
              </div>
            </div>
            <div className="col-md-4">
              <div style={{ backgroundColor: "rgba(255, 255, 255, 0.02)", padding: "16px", borderRadius: "12px", border: "1px solid rgba(255, 255, 255, 0.05)" }}>
                <p style={{ fontSize: "11px", textTransform: "uppercase", color: "rgba(255, 255, 255, 0.4)", letterSpacing: "0.05em", margin: "0 0 8px 0" }}>Period Change</p>
                <div className="d-flex align-items-center gap-2">
                  <p style={{
                    fontSize: "28px",
                    fontWeight: "bold",
                    margin: 0,
                    fontFamily: "monospace",
                    color: metric.pct >= 0 ? "#10b981" : "#ef4444"
                  }}>
                    {metric.pct >= 0 ? "+" : ""}{metric.pct.toFixed(2)}%
                  </p>
                  {metric.pct >= 0 ? <TrendingUp size={24} color="#10b981" /> : <TrendingDown size={24} color="#ef4444" />}
                </div>
              </div>
            </div>
          </div>

          <div style={{ height: "350px", width: "100%", padding: "10px", backgroundColor: "rgba(255, 255, 255, 0.01)", borderRadius: "16px" }}>
            <ZoomableMetricChart metric={metric} height={330} fmtValue={fmtValue} />
          </div>

          <div style={{ marginTop: "24px", padding: "16px", borderRadius: "12px", backgroundColor: "rgba(255, 255, 255, 0.03)", border: "1px solid rgba(255, 255, 255, 0.05)" }}>
            <h4 style={{ fontSize: "12px", fontWeight: "600", color: "white", marginBottom: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
              <Info size={14} style={{ color: "#F6255A" }} /> What does this mean?
            </h4>
            <p style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.6)", lineHeight: "1.6", margin: 0 }}>
              {metric.explanation}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};


export default function GameDashboard({
  appid,
  appListData,
  trackedApp,
  selectedTab,
  onAppSelect,
}) {
  const { trackEvent } = useAnalytics()
  const [appdetail, setAppdetail] = useState([])
  const [expandedChartMetric, setExpandedChartMetric] = useState(null)
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const gameID = queryParams.get("id")
  const openGraphParam = queryParams.get("openGraph")
  const openToolParam = queryParams.get("open")
  const params = useParams()
  const appName = params["*"]
  const navigate = useNavigate()
  // const { params } = useParams()
  const { app_id, data, selected_tab, source_surface, search_keyword } = location.state || {}
  const [comparisonList, setComparisonList] = useState([])
  const [comparisonBetween, setComparisonBetween] = useState([])
  const [showPlanModal, setShowPlanModal] = useState(false)
  const [showTrackFirstModal, setShowTrackFirstModal] = useState(false)
  const [appInfoCache, setAppInfoCache] = useState(() => {
    try {
      const cached = localStorage.getItem('aiReportCache')
      return cached ? JSON.parse(cached) : {}
    } catch (error) {
      console.error('Error loading AI report cache from localStorage:', error)
      return {}
    }
  })
  const [showAIReportSeparately, setShowAIReportSeparately] = useState(false)
  const [showForecastingSeparately, setShowForecastingSeparately] = useState(false)
  const [showAICMOSeparately, setShowAICMOSeparately] = useState(false)
  const [showSocialComparisonSeparately, setShowSocialComparisonSeparately] = useState(false)

  // Switching into any of these four "separate tool view" panels swaps content
  // in place (no route change), so the browser keeps whatever scroll position
  // the main App Store Listing page was at - the panel would open mid-scroll
  // instead of at its own top. Jump back to the top whenever one opens.
  useEffect(() => {
    if (showAIReportSeparately || showForecastingSeparately || showAICMOSeparately || showSocialComparisonSeparately) {
      window.scrollTo(0, 0)
    }
  }, [showAIReportSeparately, showForecastingSeparately, showAICMOSeparately, showSocialComparisonSeparately])
  const [aiQuery, setAiQuery] = useState("")
  const [askOpen, setAskOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false);
  const toggleAsk = () => setAskOpen(prev => !prev)

  // Identify if current session is browsing as a Guest, or isn't logged in
  // at all - anonymous visitors get the same restricted view as guests.
  const isGuest = useMemo(() => {
    try {
      const authToken = localStorage.getItem("authToken")
      if (!authToken) return true
      const authUser = JSON.parse(localStorage.getItem("authUser"))
      return (
        authUser?.data?.is_guest === true || authUser?.is_guest === true
      )
    } catch (_) {
      return false
    }
  }, [])

  const [guestTrackAttempted, setGuestTrackAttempted] = useState(false)

  // Policy Checker states
  const [policyData, setPolicyData] = useState(null)
  const [policyLoading, setPolicyLoading] = useState(false)
  // Untrack UI removed: we no longer show an Untrack button/modal
  const [showLightbox, setShowLightbox] = useState(false)
  const [lightboxImage, setLightboxImage] = useState('')
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportMetricType, setExportMetricType] = useState('')
  const [selectedFormat, setSelectedFormat] = useState('csv')
  const storedComparisonApps = (() => {
    try {
      const storedData = sessionStorage.getItem("comparisonBetween")
      if (storedData && storedData !== "null" && storedData !== "undefined") {
        return JSON.parse(storedData)
      }
      return []
    } catch (error) {
      console.error("Error parsing stored comparison apps:", error)
      return []
    }
  })()
  const storedCamparisonList = sessionStorage.getItem("comparisonList")
  const [loading, setLoading] = useState(false)
  const [btnLoader, setBtnLoader] = useState(false)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [notFound, setNotFound] = useState(false)
  const [revenueTooltipOpen, setRevenueTooltipOpen] = useState(false)
  const [tooltipModalOpen, setTooltipModalOpen] = useState(false)
  const [tooltipModalContent, setTooltipModalContent] = useState(null)
  const [activeMaturityTooltip, setActiveMaturityTooltip] = useState(null)

  const goBackButtonStyle = {
    backgroundColor: "rgba(50, 50, 60, 0.6)",
    border: "1px solid rgba(255, 255, 255, 0.12)",
    color: "#FF4B7D",
    padding: "8px 16px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "13px",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
    backdropFilter: "blur(10px)",
    transition: "background-color 0.15s ease, border-color 0.15s ease, transform 0.1s ease",
  }

  const trackingActionButtonStyle = {
    padding: "8px 20px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "13px",
    border: "2px solid transparent",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    color: "#ffffff",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    minHeight: 36,
    minWidth: "max-content",
    maxWidth: "100%",
    overflow: "visible",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.45)",
    transition: "all 0.25s ease",
  }

  // URL Validation and Correction
  useEffect(() => {
    // Skip URL correction if coming from sponsored ad
    if (location.state?.preventUrlCorrection) {
      return
    }
    
    if (appdetail && (appdetail.name || appdetail.item_id)) {
      // getAppDetailsLink builds the one canonical shape (genre-slug +
      // name-slug) also used by listing-page links, the SEO canonical tag,
      // and the sitemap generator - keeping this in sync with those instead
      // of the raw, unslugified `game_url` field is what avoids redirecting
      // to a different-looking URL than the one that was actually linked.
      const expectedPath = getAppDetailsLink(appdetail);
      const expectedPathname = expectedPath.split('?')[0];
      const currentFull = location.pathname + location.search;

      if (!currentFull.startsWith(expectedPathname)) {
        navigate(expectedPath, {
          replace: true,
          state: location.state
        });
      }
    }
  }, [appdetail, appName, navigate, location.state, location.pathname, location.search]);

  useEffect(() => {
    if (gameID) {
      fetchAppDetails(gameID)
      fetchTrackedAppList()
      fetchAppHistory(gameID)
    }
  }, [gameID])

  // Load AI report data from cache when navigating to a different app
  useEffect(() => {
    if (gameID) {
      console.log('Loading cache for gameID:', gameID, 'Cache:', appInfoCache)
      setAppInfo(appInfoCache[gameID] || null)
      setShowAIReportSeparately(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameID])

  // Refresh tracked apps when page becomes visible (user navigates back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && gameID) {
        callAppTrackapi() // Always fetch fresh data when page becomes visible
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Also refresh on window focus
    const handleFocus = () => {
      if (gameID) {
        callAppTrackapi()
      }
    }

    window.addEventListener('focus', handleFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [gameID])

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showLightbox) {
        if (e.key === 'Escape') {
          closeLightbox()
        } else if (e.key === 'ArrowRight') {
          nextImage()
        } else if (e.key === 'ArrowLeft') {
          prevImage()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [showLightbox, lightboxIndex, appdetail?.screenshots])

  //tracked apps

  const fetchTrackedAppList = () => {
    try {
      // Prefer namespaced session storage helper (safe across users),
      // fall back to raw sessionStorage and then localStorage if present.
      let parsedData = null
      try {
        const s = getSessionItem('comparisonBetween')
        if (s && Array.isArray(s)) parsedData = s
      } catch (err) { }

      if (!parsedData) {
        try {
          const raw = sessionStorage.getItem('comparisonBetween')
          if (raw && raw !== 'null' && raw !== 'undefined') parsedData = JSON.parse(raw)
        } catch (err) { }
      }

      if (!parsedData) {
        try {
          const l = getLocalItem('comparisonBetween')
          if (l && Array.isArray(l)) parsedData = l
        } catch (err) { }
      }

      if (Array.isArray(parsedData) && parsedData.length > 0) {
        setComparisonList(parsedData)
        setComparisonBetween(parsedData)
      } else {
        callAppTrackapi()
      }
    } catch (error) {
      console.error('Error parsing stored comparison data:', error)
      callAppTrackapi()
    }
  }

  const callAppTrackapi = async () => {
    try {
      const token = localStorage.getItem("authToken")
      const headers = {}
      if (token) headers.Authorization = `Bearer ${token}`

      const res = await axios.get(`${api}/tracker/tracking/app`, { headers })
      setComparisonList(res.data.games)
      sessionStorage.setItem("comparisonBetween", JSON.stringify(res.data.games))
    } catch (err) {
      console.error('Error fetching tracked apps list:', err)
    }
  }

  const fetchAppDetails = async (app_id) => {
    setLoading(true)
    setNotFound(false)
    try {
      const token = localStorage.getItem("authToken")
      const headers = {}
      if (token) headers.Authorization = `Bearer ${token}`

      const res = await axios.get(`${api}/tracker/app/${app_id}`, { headers })

      // Check if details exists and is not an empty object
      if (res.data && res.data.details && Object.keys(res.data.details).length > 0) {
        setAppdetail(res.data.details)
        setLastUpdated(new Date().toISOString())
        setNotFound(false)
        trackEvent(ANALYTICS_EVENTS.FEATURE.APP_VIEWED, {
          app_id,
          app_name: res.data.details.name || data?.name,
          platform: res.data.details.platform,
          source_surface,
          search_keyword,
        })
      } else if (res.data && res.data.details && Object.keys(res.data.details).length === 0) {
        // Explicitly handle empty details object as Not Found
        setAppdetail({})
        setNotFound(true)
      } else if (res.data && res.data.status === 200) {
        // Fallback for other 200 responses where details might be missing but data is valid?
        // Or if details is null/undefined but status is 200.
        setAppdetail(res.data.details || res.data)
        setLastUpdated(new Date().toISOString())
        setNotFound(false)
        trackEvent(ANALYTICS_EVENTS.FEATURE.APP_VIEWED, {
          app_id,
          app_name: (res.data.details || res.data)?.name || data?.name,
          platform: (res.data.details || res.data)?.platform,
          source_surface,
          search_keyword,
        })
      } else {
        console.error("No details found in API response")
        setAppdetail({})
        setNotFound(true)
      }
    } catch (error) {
      console.error("Error fetching app details:", error)
      // For any error, we should probably consider it not found or failed
      setNotFound(true)
      setAppdetail({})
    } finally {
      setLoading(false)
    }
  }

  const trackNewApp = async app => {
    if (isGuest) {
      setGuestTrackAttempted(true)
      return { success: false, reason: 'guest_restricted' }
    }

    const isLoggedIn = localStorage.getItem("authUser") || localStorage.getItem("authToken")
    if (!isLoggedIn) {
      navigateToLogin(location.pathname + location.search, navigate)
      return
    }

    const appExists = comparisonList.some(
      item => item.game_details.item_id === app
    )
    if (!appExists) {
      const gameid = { item_id: app }
      try {
        const token = localStorage.getItem("authToken")
        const headers = { "Content-Type": "application/json" }
        if (token) headers.Authorization = `Bearer ${token}`
        const res = await axios.post(`${api}/tracker/tracking/app/`, gameid, { headers })

        // ✅ Handle API “soft error” inside 200 response
        if (res.data?.status === 400 && res.data?.action === "UPGRADE") {
          setShowPlanModal(true)
          setBtnLoader(false)  // Reset button loading state
          return { success: false, reason: 'upgrade_required' }
        }
        // Success: refresh the tracked apps list
        await callAppTrackapi()
        setBtnLoader(false)
        return { success: true }
      } catch (error) {
        console.error("Error sending data:", error)

        // Check if it's an upgrade limit error in HTTP error response
        if (error.response?.data?.status === 400 && error.response?.data?.action === "UPGRADE") {
          setShowPlanModal(true)
        }

        setBtnLoader(false)
        return { success: false, reason: 'error' }
      }
    } else {
      removeApp(app)
      return { success: true }
    }
  }

  async function removeApp(val) {
    try {
      const token = localStorage.getItem("authToken")
      const headers = {}
      if (token) headers.Authorization = `Bearer ${token}`
      const res = await axios.delete(`${api}/tracker/tracking/app.remove`, { headers, data: { item_id: val } })
      // Success: refresh the tracked apps list
      await callAppTrackapi()
      setBtnLoader(false)
    } catch (error) {
      console.error("Error sending data:", error)
      setBtnLoader(false)
    }
  }
  const ratingData = data?.rating
  const istotalratingAvailable = ratingData?.total_rating || false
  var overallRating = 0.0
  if (istotalratingAvailable && ratingData?.rating_index) {
    overallRating = (
      (ratingData.rating_index["1"] +
        ratingData.rating_index["2"] * 2 +
        ratingData.rating_index["3"] * 3 +
        ratingData.rating_index["4"] * 4 +
        ratingData.rating_index["5"] * 5) /
      ratingData.total_rating
    ).toFixed(1)
  }
  const formatDate = dateString => {
    const date = new Date(dateString)

    // Extract individual components
    const day = String(date.getDate()).padStart(2, "0")
    const month = String(date.getMonth() + 1).padStart(2, "0") // getMonth() returns 0-based month
    const year = String(date.getFullYear()).slice(-2) // Get last 2 digits of the year
    let hours = date.getHours()
    const minutes = String(date.getMinutes()).padStart(2, "0")

    // Determine AM or PM
    const period = hours >= 12 ? "PM" : "AM"

    // Convert 24-hour format to 12-hour format
    hours = hours % 12
    hours = hours ? String(hours).padStart(2, "0") : "12" // 0 should be displayed as 12

    // Format in dd-mm-yy hh:mm AM/PM
    return `${day}-${month}-${year} ${hours}:${minutes} ${period}`
  }

  const formatDateToDisplay = dateString => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    const day = String(date.getDate()).padStart(2, "0")
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const year = date.getFullYear()
    return `${day}-${month}-${year}`
  }

  const getRevenueRange = revenue => {
    if (!revenue || revenue <= 0) return "$0"

    if (revenue < 10000) return "< $10k"
    if (revenue < 25000) return "$10k – $25k"
    if (revenue < 50000) return "$25k – $50k"
    if (revenue < 100000) return "$50k – $100k"
    if (revenue < 250000) return "$100k – $250k"
    if (revenue < 500000) return "$250k – $500k"
    if (revenue < 1000000) return "$500k – $1M"
    if (revenue < 25000000) return "$1M – $25M"
    if (revenue < 50000000) return "$25M – $50M"
    if (revenue < 100000000) return "$50M – $100M"
    if (revenue < 500000000) return "$100M+"
    return "$500M+"
  }

  const formatFileSize = bytes => {
    if (!bytes) return "N/A"
    const sizes = ["Bytes", "KB", "MB", "GB"]
    if (bytes === 0) return "0 Bytes"
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i]
  }

  // App size may arrive pre-formatted with a unit (e.g. "376 MB", "1.2 GB")
  // or as a raw byte count - display either as-is, or null if unavailable.
  const getSizeDisplay = size => {
    if (size === null || size === undefined || size === "") return null
    if (typeof size === "string" && /[a-zA-Z]/.test(size)) return size.trim()
    const bytes = Number(size)
    if (!bytes || isNaN(bytes)) return null
    return formatFileSize(bytes)
  }

  const getPriceDisplay = price => {
    if (!price) return "Free"
    if (price.amount === 0) return "Free"
    return `$${price.amount} ${price.currency}`
  }

  const getRatingDisplay = rating => {
    if (!rating || !rating.average) return "N/A"
    return rating.average.toFixed(1)
  }

  const getDownloadsDisplay = downloads => {
    if (downloads == null || downloads === "") return "N/A"

    const num = Number(downloads)
    if (num === 0) return "0"
    if (isNaN(num)) return "N/A"

    if (num >= 1000000) {
      return (num / 1000000).toFixed(2).replace(/\.?0+$/, '') + "M"
    } else if (num >= 1000) {
      return (num / 1000).toFixed(2).replace(/\.?0+$/, '') + "K"
    }

    // For numbers less than 1000, show as integer or with minimal decimals
    return num % 1 === 0 ? num.toString() : num.toFixed(0)
  }

  const getRevenueDisplay = revenue => {
    // Accept numbers, numeric strings (including with commas/currency), and simple objects like { amount }
    if (revenue == null) return "$0"

    let num = revenue

    if (typeof revenue === "string") {
      // strip non-numeric characters except dot and minus
      const cleaned = revenue.replace(/[^0-9.-]+/g, "")
      num = cleaned === "" ? NaN : Number(cleaned)
    } else if (typeof revenue === "object") {
      // try common shape
      if (typeof revenue.amount === "number") num = revenue.amount
      else if (typeof revenue.amount === "string") num = Number(revenue.amount.replace(/[^0-9.-]+/g, ""))
      else num = NaN
    }

    if (!Number.isFinite(num) || num <= 0) return "$0"

    if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(1)}M`
    } else if (num >= 1000) {
      return `$${(num / 1000).toFixed(1)}K`
    }

    // For small numbers, show rounded integer dollars
    return `$${Math.round(num).toString()}`
  }

  // Placeholder string indicating gated metrics from API
  const LOCK_PLACEHOLDER = "%UPGRADE_KEYQ%";

  // Resolve a raw metric value from multiple possible response shapes
  const resolveMetricRaw = (metricName) => {
    if (!appdetail) return null
    const tryPaths = [
      appdetail[metricName],
      appdetail?.stats?.[metricName],
      appdetail?.game_details?.[metricName],
      appdetail?.game_details?.stats?.[metricName],
      appdetail?.[metricName]?.value,
      appdetail?.stats?.[metricName]?.value,
      appdetail?.game_details?.[metricName]?.value,
      appdetail?.game_details?.stats?.[metricName]?.value,
    ]
    for (const v of tryPaths) {
      if (v !== undefined && v !== null) return v
    }
    return null
  }

  const formatNumber = (num, decimals = 2) => {
    if (num >= 1e6) {
      return (num / 1e6).toFixed(decimals) + " M"
    } else if (num >= 1e3) {
      return (num / 1e3).toFixed(decimals) + " K"
    }
    return num.toFixed(decimals)
  }

  const formatDecimal = (num, decimals = 2) => {
    const numValue = Number(num)
    if (isNaN(numValue)) return "N/A"
    return formatNumber(numValue, decimals)
  }

  const getAnalyticsValue = (analytics, key) => {
    if (!analytics || !analytics[key]) return "N/A"
    const value = analytics[key]
    if (key === "asr" || key === "aur") {
      const numValue = Number(value)
      if (isNaN(numValue)) return "N/A"
      return formatDecimal(numValue, 2)
    } else if (key === "ktr") {
      const numValue = Number(value)
      if (isNaN(numValue)) return "N/A"
      const ktrValue = numValue * 1000
      return formatNumber(ktrValue, 2)
    } else if (key === "ku") {
      const numValue = Number(value)
      if (isNaN(numValue)) return "N/A"
      return numValue.toFixed(0)
    } else if (key === "d" || key === "urv") {
      const numValue = Number(value)
      if (isNaN(numValue)) return "N/A"
      return numValue.toFixed(0)
    } else if (key === "revenue") {
      return getRevenueDisplay(value)
    }
    return value.toString()
  }

  const getGenreDisplay = genre => {
    if (!genre || !Array.isArray(genre)) return "N/A"
    return genre.join(", ")
  }

  const getSupportedPlatformsDisplay = platforms => {
    if (!platforms || !Array.isArray(platforms)) return "N/A"
    return platforms.join(", ")
  }

  const getPlatformDisplayName = platform => {
    if (!platform) return "N/A"
    const p = platform.toLowerCase()
    if (p === 'apple_vision' || p === 'apple vision' || p === 'vision pro' || p === 'visionos') return "Apple Vision"
    if (p === 'meta' || p === 'oculus' || p === 'quest') return "Meta"
    return platform.charAt(0).toUpperCase() + platform.slice(1)
  }

  const formatDataMaturityNote = note => {
    if (!note || typeof note !== 'string') return ''
    return note.replace(/\\u2014/g, '—').trim()
  }

  useEffect(() => {
    setBtnLoader(false)
  }, [comparisonList])

  const itemIds = comparisonList.map(app => app.game_details?.item_id)

  // Auto-trigger tracking when navigated from a "Track Now" intent
  const autoTrackTriggeredRef = useRef(false)
  useEffect(() => {
    if (!gameID) return
    if (autoTrackTriggeredRef.current) return
    // Only auto-track when explicitly coming from a Track Now action
    if (location?.state?.fromTrackNow === true && location.state.app_id === gameID) {
      const alreadyTracked = itemIds.includes(gameID) || itemIds.includes(Number(gameID))
      if (!alreadyTracked) {
        autoTrackTriggeredRef.current = true
        setBtnLoader(true)
        trackNewApp(gameID)
      }
    }
  }, [gameID, location?.state, itemIds])

  const initialTab = (() => {
    // If the route sent a selected_tab index, prefer that
    if (typeof selected_tab !== 'undefined' && selected_tab !== null) return selected_tab
    // If navigation state requested opening the app score graph, show the Tracking tab (index 2)
    if (location?.state?.openGraph === 'app_score' || openGraphParam === 'app_score') return 2
    return 0
  })()

  const [activeTab, setActiveTab] = useState(initialTab)


  const handleGoBack = () => {
    // Prefer explicit from state (set by callers), otherwise use history, then fallback to dashboard
    try {
      const from = location && location.state && location.state.from
      if (from) {
        navigate(from)
        return
      }
    } catch (err) {
      // ignore
    }
    if (window && window.history && window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/dashboard')
    }
  }

  // Handler for Track/Show Keyword for this App
  const handleKeywordTracking = async () => {
    try {
      if (itemIds.includes(appdetail?.item_id)) {
        // Already tracked, redirect to keyword details page
        // navigate('/keyworddetails', {
        //   state: {
        //     from: location.pathname,
        //     item_id: appdetail?.item_id,
        //     appName: appdetail?.name,
        //     appId: appdetail?.item_id,
        //     selectedAppId: appdetail?.item_id
        //   }
        // })
        navigate(
          `/xr/keyword/${generateSlug(appdetail?.name)}?id=${appdetail?.item_id}`,
          { state: { from: location.pathname } }
        )
      } else {
        // Not tracked, create new tracked record first
        setBtnLoader(true)
        const result = await trackNewApp(appdetail?.item_id)

        // Only navigate if tracking was successful
        if (result?.success) {
          // navigate('/keyworddetails', {
          //   state: {
          //     from: location.pathname,
          //     item_id: appdetail?.item_id,
          //     appName: appdetail?.name,
          //     appId: appdetail?.item_id,
          //     selectedAppId: appdetail?.item_id,
          //     justTracked: true
          //   }
          // })
          navigate(
            `/xr/keyword/${generateSlug(appdetail?.name)}?id=${appdetail?.item_id}&justTracked=1`,
            { state: { from: location.pathname, justTracked: true } }
          )
        }
        // If tracking failed (upgrade required or error), stay on current page
      }
    } catch (error) {
      console.error('Error in keyword tracking navigation:', error)
      setBtnLoader(false)
    }
  }

  // Handler for Track/Show Category for this App
  const handleCategoryTracking = async () => {
    try {
      if (itemIds.includes(appdetail?.item_id)) {
        // Already tracked, redirect to category details page
        // navigate('/Categorydetails', {
        //   state: {
        //     from: location.pathname,
        //     item_id: appdetail?.item_id,
        //     appName: appdetail?.name,
        //     appId: appdetail?.item_id,
        //     selectedAppId: appdetail?.item_id
        //   }
        // })
        navigate(
          `/xr/category/${generateSlug(appdetail?.name)}?id=${appdetail?.item_id}`,
          { state: { from: location.pathname } }
        )
      } else {
        // Not tracked, create new tracked record first
        setBtnLoader(true)
        const result = await trackNewApp(appdetail?.item_id)

        // Only navigate if tracking was successful
        if (result?.success) {
          // navigate('/Categorydetails', {
          //   state: {
          //     from: location.pathname,
          //     item_id: appdetail?.item_id,
          //     appName: appdetail?.name,
          //     appId: appdetail?.item_id,
          //     selectedAppId: appdetail?.item_id,
          //     justTracked: true
          //   }
          // })
          navigate(
            `/xr/category/${generateSlug(appdetail?.name)}?id=${appdetail?.item_id}&justTracked=1`,
            { state: { from: location.pathname, justTracked: true } }
          )
        }
        // If tracking failed (upgrade required or error), stay on current page
      }
    } catch (error) {
      console.error('Error in genre tracking navigation:', error)
      setBtnLoader(false)
    }
  }

  // Handle lightbox screenshot viewing
  const openLightbox = (imageUrl, index) => {
    setLightboxImage(imageUrl)
    setLightboxIndex(index)
    setShowLightbox(true)
  }

  const closeLightbox = () => {
    setShowLightbox(false)
    setLightboxImage('')
    setLightboxIndex(0)
  }

  const nextImage = () => {
    if (appdetail?.screenshots && lightboxIndex < appdetail.screenshots.length - 1) {
      const newIndex = lightboxIndex + 1
      setLightboxIndex(newIndex)
      setLightboxImage(appdetail.screenshots[newIndex])
    }
  }

  const prevImage = () => {
    if (appdetail?.screenshots && lightboxIndex > 0) {
      const newIndex = lightboxIndex - 1
      setLightboxIndex(newIndex)
      setLightboxImage(appdetail.screenshots[newIndex])
    }
  }

  // Helper function to check if analytics value requires upgrade
  const requiresUpgrade = (analytics, key) => {
    return analytics && analytics[key] === '%UPGRADE_KEYQ%'
  }

  // Helper function to check if any analytics require upgrade
  const hasUpgradeValues = (analytics) => {
    if (!analytics) return false
    const keys = ['ktr', 'ku', 'aur', 'd', 'urv', 'asr', 'revenue']
    return keys.some(key => analytics[key] === '%UPGRADE_KEYQ%')
  }

  // Get premium/free rows for analytics table
  const getAnalyticsRows = (analytics) => {
    const allRows = [
      { key: 'asr', label: 'App Store Score' },
      { key: 'aur', label: 'Average User Rating' }, // Top 2 (always visible)
      { key: 'ktr', label: 'Keyword Relevance' },
      { key: 'ku', label: 'Keyword Usage' },
      { key: 'd', label: 'Downloads' },
      { key: 'urv', label: 'User Reviews Volume' },
      { key: 'revenue', label: 'Total Revenue' }
    ]

    const hasUpgrade = hasUpgradeValues(analytics)

    if (hasUpgrade) {
      // For upgrade users, show only top 2 rows without blur
      return {
        visibleRows: allRows.slice(0, 2),
        hiddenRows: allRows.slice(2),
        needsUpgrade: true
      }
    } else {
      // For subscribed users, show all rows
      return {
        visibleRows: allRows,
        hiddenRows: [],
        needsUpgrade: false
      }
    }
  }

  // Export modal functions
  const openExportModal = (metricType, event) => {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    setExportMetricType(metricType)
    setSelectedFormat('csv')
    setShowExportModal(true)
  }

  const closeExportModal = () => {
    setShowExportModal(false)
    setExportMetricType('')
    setSelectedFormat('csv')
  }

  const handleExportSubmit = () => {
    exportData(exportMetricType, selectedFormat)
    closeExportModal()
  }

  // Export data function
  const exportData = (metricType, format) => {

    // Create filename with app name and timestamp
    const appName = appdetail?.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'app'
    const timestamp = new Date().toISOString().split('T')[0]
    const filename = `${appName}_${metricType}_${timestamp}`

    // Prepare data based on metric type
    let exportDataObj = {
      appName: appdetail?.name || 'Unknown App',
      metricType,
      timestamp: new Date().toISOString(),
      analytics: appdetail?.game_details?.analytics
    }

    if (format === 'csv') {
      exportAsCSV(exportDataObj, filename, metricType)
    } else if (format === 'pdf') {
      exportAsPDF(exportDataObj, filename, metricType)
    } else if (format === 'xlsx') {
      exportAsXLSX(exportDataObj, filename, metricType)
    }
  }

  const exportAsCSV = (data, filename, metricType) => {
    let csvContent = ''

    if (metricType === 'analytics') {
      csvContent = 'Metric,Value\n'
      csvContent += `App Store Score,${getAnalyticsValue(data.analytics, "asr")}\n`
      csvContent += `Keyword Relevance,${getAnalyticsValue(data.analytics, "ktr")}\n`
      csvContent += `Keyword Usage,${getAnalyticsValue(data.analytics, "ku")}\n`
      csvContent += `Downloads,${getAnalyticsValue(data.analytics, "d")}\n`
      csvContent += `Average User Rating,${getAnalyticsValue(data.analytics, "aur")}\n`
      csvContent += `User Reviews Volume,${getAnalyticsValue(data.analytics, "urv")}\n`
      csvContent += `Total Revenue,${getAnalyticsValue(data.analytics, "revenue")}\n`
    } else {
      // Individual metric export
      const metricNames = {
        'asr': 'App Store Score',
        'ktr': 'Keyword Relevance',
        'ku': 'Keyword Usage',
        'd': 'Daily Downloads',
        'downloads': 'Daily Downloads',
        'aur': 'Average User Rating'
      }

      const metricName = metricNames[metricType] || metricType
      const currentValue = formatMetricValue(metricType === 'downloads' ? 'd' : metricType, getMetricStats(metricType === 'downloads' ? 'd' : metricType).current)
      const changeValue = formatChangePct(getMetricStats(metricType === 'downloads' ? 'd' : metricType).changePct)

      csvContent = 'Metric,Current Value,Change\n'
      csvContent += `${metricName},${currentValue},${changeValue}\n`
      csvContent += `\nApp: ${data.appName}\n`
      csvContent += `Export Date: ${new Date(data.timestamp).toLocaleString()}\n`
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `${filename}.csv`
    link.click()
  }

  const exportAsPDF = (data, filename, type) => {
    // For now, create a simple text-based PDF content
    alert('PDF export functionality will be implemented with a PDF library')
  }

  const exportAsXLSX = (data, filename, type) => {
    // For now, fallback to CSV format
    alert('XLSX export functionality will be implemented with a spreadsheet library')
  }

  // Untrack flow removed per latest requirement (hide Untrack)

  // Get tracking information for current app
  const getTrackingInfo = () => {
    if (!comparisonList || !appdetail?.item_id) return null

    const trackedApp = comparisonList.find(
      item => item.game_details.item_id === appdetail.item_id
    )

    if (trackedApp) {
      // Format tracking date (assuming there's a created_at or similar field)
      const trackingDate = trackedApp.created_at || trackedApp.tracked_date || new Date().toISOString()
      const formattedDate = new Date(trackingDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })

      // Generate consistent percentage based on app ID (so it doesn't change randomly)
      const appIdHash = appdetail.item_id.toString()
      const hashSum = appIdHash.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)
      const consistentPercentage = (hashSum % 25) + 5 // Generates 5-29% consistently based on app ID

      return {
        isTracked: true,
        trackingDate: formattedDate,
        userPercentage: consistentPercentage
      }
    }

    return { isTracked: false }
  }

  const data1 = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    datasets: [
      {
        label: "Monthly Downloads",
        data: [120, 190, 300, 250, 320, 400],
        borderColor: "rgba(75,192,192,1)",
        backgroundColor: "rgba(75,192,192,0.2)",
        tension: 0.4,
        fill: false,
      },
    ],
  }

  // Chart options
  const options = {
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: "Line Chart - Game Analytics",
        font: {
          size: 18,
        },
      },
      legend: {
        display: true,
        position: "top",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  }
  const [appInfo, setAppInfo] = useState()
  const [sentimentData, setSentimentData] = useState()
  const [sentimentLoading, setSentimentLoading] = useState(false)
  const [sentimentTimeFilter, setSentimentTimeFilter] = useState('30')
  const isAppdetailLoaded = appdetail && typeof appdetail === 'object' && Object.keys(appdetail).length > 0
  // AI Reports optional date range with defaults: last 7 days
  const formatDateYYYYMMDD = date => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }
  const [aiStartDate, setAiStartDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 30)
    return formatDateYYYYMMDD(d)
  })
  const [aiEndDate, setAiEndDate] = useState(() =>
    formatDateYYYYMMDD(new Date())
  )
  const [aiGenerating, setAiGenerating] = useState(false)
  // Tracks which gameIDs the auto-load effect below has already attempted (success or failure),
  // so a failed fetch (e.g. plan restriction) doesn't retry in a loop on every re-render.
  const aiReportAutoAttemptedRef = useRef({})
  const [aiCacheLoading, setAiCacheLoading] = useState(false)
  const [aiExporting, setAiExporting] = useState(false)

  const getAiRangeSummary = () => {
    if (!aiStartDate || !aiEndDate) return ""
    const start = new Date(aiStartDate)
    const end = new Date(aiEndDate)
    const diffTime = Math.abs(end - start)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return `Last ${diffDays} Days`
  }

  const fetchSentimentData = useCallback(async () => {
    if (!appdetail?.item_id) return

    try {
      setSentimentLoading(true)
      const token = localStorage.getItem("authToken")
      const headers = { "Content-Type": "application/json" }
      if (token) headers.Authorization = `Bearer ${token}`

      const config = {
        method: "post",
        url: `${ai_api}/chat/app-sentiments/`,
        headers,
        data: JSON.stringify({
          item_id: appdetail.item_id,
        }),
      }

      const { data, status } = await axios.request(config)

      if (status === 200 && data.status === "success") {
        setSentimentData(data.sentiment_summary)
      } else {
        console.error("Sentiment data fetch Failed !")
      }
    } catch (error) {
      console.error("Error fetching sentiment data:", error)
    } finally {
      setSentimentLoading(false)
    }
  }, [appdetail?.item_id])

  const fetchPolicyData = useCallback(
    async () => {
      try {
        setPolicyLoading(true)

        const payload = { app_id: `${gameID}` }
        let body = JSON.stringify(payload)

        const token = localStorage.getItem("authToken")
        const headers = { "Content-Type": "application/json" }
        if (token) headers.Authorization = `Bearer ${token}`

        const config = {
          method: "post",
          maxBodyLength: Infinity,
          url: `${ai_api}/chat/policy-check/`,
          headers,
          data: body,
        }

        const { data, status } = await axios.request(config)

        if (status === 200) {
          setPolicyData(data.policy_report)
        } else {
          console.error("Policy Data fetch Failed !")
        }
      } catch (error) {
        console.error("Error fetching policy data:", error)
      } finally {
        setPolicyLoading(false)
      }
    },
    [gameID]
  )

  const fetchAppinfo = useCallback(
    async (startDate = "", endDate = "", forceRefresh = "false", isManual = false) => {
      try {
        // Check if data is already cached and forceRefresh is false
        if (!isManual && forceRefresh === "false" && appInfoCache[gameID]) {
          setAppInfo(appInfoCache[gameID])
          return
        }
        
        if (isManual) {
          setAiGenerating(true)
        } else {
          setAiCacheLoading(true)
        }
        const payload = { app_id: `${gameID}`, force_refresh: forceRefresh }
        if (startDate) payload.start_date = startDate
        if (endDate) payload.end_date = endDate

        let body = JSON.stringify(payload)

        const token = localStorage.getItem("authToken")
        const headers = { "Content-Type": "application/json" }
        if (token) headers.Authorization = `Bearer ${token}`

        const config = {
          method: "post",
          maxBodyLength: Infinity,
          url: `${ai_api}/chat/app-info/`,
          headers,
          data: body,
        }

        const { data, status } = await axios.request(config)

        // The AI service (ai.keyquest.io) reports plan/tier restrictions as a
        // 200/4xx response with { error: "Please upgrade your plan." } — a
        // different shape from the tracker API's { status, action, current_tier }.
        if (data?.error) {
          if (/upgrade/i.test(data.error)) {
            setShowAIReportSeparately(false)
            setShowPlanModal(true)
          } else {
            console.error("App Info fetch Failed:", data.error)
          }
          return
        }

        if (status === 200 && data?.response) {
          setAppInfo(data.response)
          // Store in cache for this app
          console.log('Storing cache for gameID:', gameID, 'Data:', data.response)
          const newCache = {
            ...appInfoCache,
            [gameID]: data.response
          }
          setAppInfoCache(newCache)
          // Save to localStorage for persistence
          try {
            localStorage.setItem('aiReportCache', JSON.stringify(newCache))
          } catch (error) {
            console.error('Error saving AI report cache to localStorage:', error)
          }
        } else {
          console.error("App Info fetch Failed !")
        }
      } catch (error) {
        console.error(error)

        // Same "please upgrade" shape, surfaced as a thrown HTTP error instead
        const errMsg = error.response?.data?.error
        if (errMsg && /upgrade/i.test(errMsg)) {
          setShowAIReportSeparately(false)
          setShowPlanModal(true)
        }
      } finally {
        if (isManual) {
          setAiGenerating(false)
        } else {
          setAiCacheLoading(false)
        }
      }
    },
    [gameID, appInfoCache]
  )

  const exportAiReportPdf = useCallback(async () => {
    try {
      if (!appInfo) return
      setAiExporting(true)
      const payload = {
        app_id: `${gameID}`,
        report_text: typeof appInfo === "string" ? appInfo : JSON.stringify(appInfo),
        app_name: appdetail?.name || "App Report",
      }

      const token = localStorage.getItem("authToken")
      const headers = { "Content-Type": "application/json" }
      if (token) headers.Authorization = `Bearer ${token}`

      const config = {
        method: "post",
        url: `${ai_api}/chat/export-pdf/`,
        headers,
        data: JSON.stringify(payload),
        responseType: "blob",
      }

      const { data, status, headers: respHeaders } = await axios.request(config)
      if (status === 200 && data) {
        const blob = new Blob([data], {
          type: headers["content-type"] || "application/pdf",
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        const filename = `${(appdetail?.name || "AI-Report").replace(
          /[^a-z0-9\-\_]+/gi,
          "_"
        )}.pdf`
        link.setAttribute("download", filename)
        document.body.appendChild(link)
        link.click()
        link.parentNode && link.parentNode.removeChild(link)
        window.URL.revokeObjectURL(url)
      } else {
        console.error("Export failed")
      }
    } catch (err) {
      console.error("Export error", err)
    } finally {
      setAiExporting(false)
    }
  }, [appInfo, gameID, appdetail?.name])

  useEffect(() => {
    // Initial load for non-report data
    fetchSentimentData()
    fetchPolicyData()
  }, [fetchSentimentData, fetchPolicyData])

  // Auto-generate AI report when opened with default date range (only if not cached)
  useEffect(() => {
    if (showAIReportSeparately && !appInfo && !aiGenerating && !aiCacheLoading && isAppdetailLoaded && !appInfoCache[gameID] && !aiReportAutoAttemptedRef.current[gameID]) {
      aiReportAutoAttemptedRef.current[gameID] = true
      fetchAppinfo(aiStartDate, aiEndDate, "false", false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAIReportSeparately, appInfo, aiGenerating, aiCacheLoading, isAppdetailLoaded, gameID, aiStartDate, aiEndDate])

  // Deep-link support: if the page is opened with ?open=forecasting|ai-report|ai-cmo|social-comparison
  // (e.g. clicked from a Dashboard app preview), auto-activate that overlay once the app is loaded & tracked.
  const openToolTriggeredRef = useRef(false)
  useEffect(() => {
    if (!openToolParam || openToolTriggeredRef.current) return
    if (loading || !isAppdetailLoaded) return
    const isTracked = itemIds.includes(appdetail?.item_id)
    if (!isTracked) return
    openToolTriggeredRef.current = true
    if (openToolParam === "forecasting") setShowForecastingSeparately(true)
    else if (openToolParam === "ai-report") setShowAIReportSeparately(true)
    else if (openToolParam === "ai-cmo") setShowAICMOSeparately(true)
    else if (openToolParam === "social-comparison") setShowSocialComparisonSeparately(true)
  }, [openToolParam, loading, isAppdetailLoaded, itemIds, appdetail])

  // Additional state variables and functions from InfoTabs
  const [selectedMetric, setSelectedMetric] = useState(null)
  const [metricValue, setMetricValue] = useState(null)
  const [modal_toggle, setmodal_toggle] = useState(false)

  // Refs for scrolling to different sections
  const ktrCardRef = useRef(null)
  const kuRef = useRef(null)
  const asrCardRef = useRef(null)
  const downRef = useRef(null)
  const aurRef = useRef(null)
  const urvRef = useRef(null)

  // Scroll functions
  const scrollToKTR = () => {
    if (ktrCardRef.current) {
      ktrCardRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }
  const scrollToKU = () => {
    if (kuRef.current) {
      kuRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }
  const scrollToASR = () => {
    if (asrCardRef.current) {
      asrCardRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }
  const scrollToDownloads = () => {
    if (downRef.current) {
      downRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }
  const scrollToAur = () => {
    if (aurRef.current) {
      aurRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }
  const scrollToUrv = () => {
    if (urvRef.current) {
      urvRef.current.scrollIntoView({ behavior: "smooth" })
    }
  }

  const toggle_modal = (metric = null, value) => {
    setSelectedMetric(metric)
    setmodal_toggle(prev => !prev)
    setMetricValue(value)
  }

  // Date formatting functions
  const formatDateToYYYYMMDD = date => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  const onemonthbackDate = date => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1 - 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  // Date state variables for different charts
  const [startDate, setStartDate] = useState(onemonthbackDate(new Date()))
  const [endDate, setEndDate] = useState(formatDateToYYYYMMDD(new Date()))
  const [ktrStartDate, setKtrStartDate] = useState(onemonthbackDate(new Date()))
  const [ktrEndDate, setKtrEndDate] = useState(formatDateToYYYYMMDD(new Date()))
  const [asrStartDate, setAsrStartDate] = useState(onemonthbackDate(new Date()))
  const [asrEndDate, setAsrEndDate] = useState(formatDateToYYYYMMDD(new Date()))
  const [kuStartDate, setKuStartDate] = useState(onemonthbackDate(new Date()))
  const [kuEndDate, setKuEndDate] = useState(formatDateToYYYYMMDD(new Date()))
  const [dStartDate, setDStartDate] = useState(onemonthbackDate(new Date()))
  const [dEndDate, setDEndDate] = useState(formatDateToYYYYMMDD(new Date()))
  const [aurStartDate, setAurStartDate] = useState(onemonthbackDate(new Date()))
  const [aurEndDate, setAurEndDate] = useState(formatDateToYYYYMMDD(new Date()))
  const [urvStartDate, setUrvStartDate] = useState(onemonthbackDate(new Date()))
  const [urvEndDate, setUrvEndDate] = useState(formatDateToYYYYMMDD(new Date()))

  const [appHistoryData, setAppHistoryData] = useState([])

  const getMetricStats = useCallback(
    metricKey => {
      if (!Array.isArray(appHistoryData)) {
        return { current: null, changePct: null }
      }
      const numericRows = appHistoryData.filter(
        entry => typeof entry?.[metricKey] === "number"
      )
      if (numericRows.length === 0) {
        return { current: null, changePct: null }
      }
      const previousValue = numericRows[0][metricKey]
      const currentValue = numericRows[numericRows.length - 1][metricKey]
      const changePct =
        typeof previousValue === "number" && previousValue !== 0
          ? ((currentValue - previousValue) / Math.abs(previousValue)) * 100
          : null
      return { current: currentValue, changePct }
    },
    [appHistoryData]
  )

  const formatMetricValue = (key, value) => {
    if (value == null) return "-"
    return getAnalyticsValue({ [key]: value }, key)
  }

  const formatChangePct = pct => {
    if (pct == null || Number.isNaN(pct)) return "-"
    const rounded = Math.round(pct)
    return `${rounded > 0 ? "+" : ""}${rounded}%`
  }

  // Responsive offsets for button rows (desktop/tablet/mobile)
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  )
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  const getButtonOffsets = useCallback(() => {
    const w = viewportWidth
    if (w < 576) return { rowLeft: '-48px', firstButtonLeft: '-64px' }
    if (w < 768) return { rowLeft: '-64px', firstButtonLeft: '-80px' }
    if (w < 992) return { rowLeft: '-80px', firstButtonLeft: '-96px' }
    return { rowLeft: '-96px', firstButtonLeft: '-136px' }
  }, [viewportWidth])

  const isMobile = viewportWidth < 576

  const fetchAppHistory = async val => {
    try {
      try {
        const token = localStorage.getItem("authToken")
        const headers = {}
        if (token) headers.Authorization = `Bearer ${token}`
        const res = await axios.post(`${api}/tracker/tracking/app.history`, { item_id: val }, { headers })
        setAppHistoryData(res.data.history.historic_entries)
      } catch (err) {
        console.error('Error fetching app history (post):', err)
      }
    } catch (error) {
      console.error("Error fetching app history data:", error)
    }
  }

  // Fetch App Graph from dashboard endpoint (used to render App Score graph)
  const fetchAppGraph = async val => {
    try {
      const token = localStorage.getItem("authToken")
      const headers = { "Content-Type": "application/json" }
      if (token) headers.Authorization = `Bearer ${token}`
      const res = await axios.post(`${api}/tracker/dashboard/app_graph`, { item_id: val }, { headers })

      // Normalize response into appHistoryData so existing charts can render
      const payload = res.data?.data || res.data

      // Map response entries to include created_at_date (YYYY-MM-DD) for KTRLineChart
      let entries = []
      if (Array.isArray(payload)) entries = payload
      else if (payload?.history?.historic_entries) entries = payload.history.historic_entries
      else if (payload?.historic_entries) entries = payload.historic_entries
      else entries = []

      const mapped = entries.map(entry => {
        const epoch = entry.created_at || entry.created_at_date || entry.ts || null
        const dateObj = epoch ? new Date(Number(epoch) * 1000) : null
        const created_at_date = dateObj ? dateObj.toISOString().slice(0, 10) : entry.created_at_date || null
        return {
          ...entry,
          created_at_date,
        }
      })

      setAppHistoryData(mapped)
    } catch (error) {
      console.error("Error fetching app graph:", error)
    }
  }

  // If navigated with openGraph: 'app_score', fetch the graph and ensure Tracking tab is active
  useEffect(() => {
    if (gameID && location?.state?.openGraph === 'app_score') {
      fetchAppGraph(gameID)
      setActiveTab(2)
    }
  }, [gameID, location?.state?.openGraph])

  function convertEpochToDate(epochTime) {
    const date = new Date(epochTime * 1000)
    const day = String(date.getDate()).padStart(2, "0")
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const year = date.getFullYear().toString().slice(-4)
    const hours = String(date.getHours()).padStart(2, "0")
    const minutes = String(date.getMinutes()).padStart(2, "0")
    return `${day}-${month}-${year} ${hours}:${minutes}`
  }

  function formatText(text) {
    if (!text) return null;

    const lines = text.split('\n').filter(Boolean);

    return lines.map((line, i) => {
      const trimmed = line.trim();

      // Match markdown-like video embeds
      const videoMatch = trimmed.match(/!\[.*\]\((https?:\/\/.*\.(mp4|webm|mov|ogg)[^)]+)\)/i);

      // Match markdown headings
      const headingMatch = trimmed.match(/^#\s*(.+)/);

      const imageMatch = trimmed.match(
        /!\[.*?\]\((https?:\/\/.*\.(png|jpg|jpeg|webp|gif)[^)]+)\)/i
      );

      if (imageMatch) {
        const imageUrl = imageMatch[1];

        return (
          <div
            key={i}
            style={{
              display: 'flex',
              justifyContent: 'center',
              margin: '28px 0',
            }}
            className="image-wrapper"
          >
            <img
              src={imageUrl}
              alt=""
              loading="lazy"
              style={{
                width: '100%',
                maxWidth: '480px',
                borderRadius: '16px',
                objectFit: 'cover',
              }}
            />
          </div>
        );
      }


      if (videoMatch) {
        const videoUrl = videoMatch[1];
        return (
          <div
            key={i}
            style={{
              display: 'flex',
              justifyContent: 'center',
              margin: '28px 0',
            }}
            className="video-wrapper"
          >
            <video
              src={videoUrl}
              controls
              playsInline
              style={{
                width: '100%',
                maxWidth: '480px',
                borderRadius: '16px',
                background: '#000',
              }}
            />
          </div>
        );
      }



      if (headingMatch) {
        const title = headingMatch[1];
        return (
          <h3
            key={i}
            style={{
              marginTop: '36px',
              marginBottom: '10px',
              fontSize: '22px',
              color: '#fff',
              fontWeight: 700,
              textShadow: '0 0 8px rgba(255,255,255,0.25)',
              letterSpacing: '0.3px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            {title}
          </h3>
        );
      }

      // Normal paragraph
      return (
        <p
          key={i}
          style={{
            margin: '6px 0',
            fontSize: '16px',
            lineHeight: 1.7,
            color: '#e0e0e0',
            maxWidth: '720px',
            textAlign: 'justify',
            textShadow: '0 0 6px rgba(255,255,255,0.1)',
          }}
        >
          {trimmed}
        </p>
      );
    });
  }



  const tabs = [
    {
      label: "About",
      icon: FaInfoCircle,
      content: (
        <div className="row">
          <div className="col-12 col-xxl-4 col-xl-6 col-lg-6 col-md-12 col-sm-12">
            <div className="release_details">
              <h2>
                <img src={rocket} alt="" /> Release Details
              </h2>
              {/* Show only skeletons until appdetail has data */}
              {!isAppdetailLoaded ? (
                <div className="py-3">
                  <SkeletonParagraph lines={6} />
                </div>
              ) : (
                <div className="release_details_inn">
                  {appdetail?.version && (
                    <div className="release_details_inn_txt">
                      <div className="release_details_inn_txt_lt">Current Version :</div>
                      <div
                        className="release_details_inn_txt_rt release_details_inn_txt_rt--truncate"
                        title={String(appdetail.version)}
                      >
                        {appdetail.version}
                      </div>
                    </div>
                  )}
                  {getSizeDisplay(appdetail?.size) && (
                    <div className="release_details_inn_txt">
                      <div className="release_details_inn_txt_lt">File Size :</div>
                      <div className="release_details_inn_txt_rt">{getSizeDisplay(appdetail.size)}</div>
                    </div>
                  )}
                  {appdetail?.publisher && (
                    <div className="release_details_inn_txt">
                      <div className="release_details_inn_txt_lt">Publisher :</div>
                      <div className="release_details_inn_txt_rt">{appdetail.publisher}</div>
                    </div>
                  )}
                  {appdetail?.created_at && appdetail?.platform !== 'apple_vision' && (
                    <div className="release_details_inn_txt">
                      <div className="release_details_inn_txt_lt">Release date :</div>
                      <div className="release_details_inn_txt_rt">{formatDateToDisplay(appdetail.created_at)}</div>
                    </div>
                  )}
                  {appdetail?.age_rating && (
                    <div className="release_details_inn_txt">
                      <div className="release_details_inn_txt_lt">Age Rating :</div>
                      <div className="release_details_inn_txt_rt">{appdetail.age_rating}</div>
                    </div>
                  )}
                  {Array.isArray(appdetail?.genre) && appdetail.genre.length > 0 && (
                    <div className="release_details_inn_txt">
                      <div className="release_details_inn_txt_lt">Genre :</div>
                      <div className="release_details_inn_txt_rt">{getGenreDisplay(appdetail.genre)}</div>
                    </div>
                  )}
                  {Array.isArray(appdetail?.supported_platforms) && appdetail.supported_platforms.length > 0 && (
                    <div className="release_details_inn_txt">
                      <div className="release_details_inn_txt_lt">Supported Platforms :</div>
                      <div className="release_details_inn_txt_rt">{getSupportedPlatformsDisplay(appdetail.supported_platforms)}</div>
                    </div>
                  )}
                  {Array.isArray(appdetail?.game_modes) && appdetail.game_modes.length > 0 && (
                    <div className="release_details_inn_txt">
                      <div className="release_details_inn_txt_lt">Game Modes :</div>
                      <div className="release_details_inn_txt_rt">{appdetail.game_modes.join(", ")}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="col-12 col-xxl-8 col-xl-6 col-lg-6 col-md-12 col-sm-12">
            <div className="description">
              <h2>
                <img src={descrip} alt="" /> Description
              </h2>
              {/* Show only skeleton until appdetail has data */}
              {!isAppdetailLoaded ? (
                <div className="py-3">
                  <SkeletonParagraph lines={8} />
                </div>
              ) : (
                <div className="release_details_inn">
                  {appdetail?.developer && (
                    <div className="release_details_inn_txt">
                      <div className="release_details_inn_txt_lt">Developer :</div>
                      <div className="release_details_inn_txt_rt">{appdetail.developer}</div>
                    </div>
                  )}
                  {appdetail?.description && (
                    <div className="release_details_inn_txt" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                      <div className="release_details_inn_txt_lt" style={{ marginBottom: '10px' }}>Description :</div>
                      <div className="release_details_inn_txt_rt" style={{ width: '100%', maxHeight: '400px', overflowY: 'auto' }}>
                        {formatText(appdetail.description)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="col-sm-12">
            <div className="screenshot_sec">
              <h2 style={{ fontSize: '22px', fontWeight: 500 }}>
                <img src={screenshot} alt="" /> Screenshots
              </h2>
              <div className="screenshot_inn gap-3">
                {loading ? (
                  // Show spinner while loading
                  <div className="col-12 text-center py-4">
                    <Spinner color="pink" size="lg">
                      Loading...
                    </Spinner>
                  </div>
                ) : appdetail?.screenshots &&
                  appdetail.screenshots.length > 0 ? (
                  appdetail.screenshots.slice(0, 4).map((screenshot, index) => (
                    <div
                      key={index}
                      className="screenshot_inn_box"
                      style={{ cursor: 'pointer' }}
                      onClick={() => openLightbox(screenshot, index)}
                    >
                      <img src={screenshot} alt={`Screenshot ${index + 1}`} />
                    </div>
                  ))
                ) : (
                  // Show message when no screenshots available
                  <div className="col-12 text-center">
                    <p>No screenshots available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      label: "Sentiment and Ratings",
      icon: FaStar,
      content: loading || !isAppdetailLoaded || sentimentLoading ? (
        <div className="py-3">
          <SkeletonParagraph lines={12} />
        </div>
      ) : (
        <div>
          {itemIds.includes(appdetail?.item_id) ? (
            sentimentData && sentimentData.sentiment_breakdown ? (
              <div className="container-fluid">
                {/* HEADER */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h2 className="text-white mb-1" style={{ fontSize: '22px' }}>
                      User Sentiment Summary
                    </h2>
                    <p className="text-muted small mb-0">
                      Sentiment derived from user reviews across tracked
                      competitors and similar apps.
                    </p>
                  </div>
                </div>

                {/* SUMMARY + CONFIDENCE ROW */}
                <div className="row mb-4">
                  {/* SENTIMENT SUMMARY */}
                  <div className="col-lg-9 mb-3">
                    <div
                      className="p-4 rounded-4"
                      style={{
                        backgroundColor: "rgba(23, 24, 33, 1)",
                        border: "1px solid #3f4150",
                      }}
                    >
                      <div className="d-flex flex-column gap-4">
                        {/* OVERALL SENTIMENT HEADING */}
                        <h3 className="text-white mb-3" style={{ fontSize: '18px' }}>
                          Overall Sentiment:{" "}
                          <span className="text-warning">
                            {sentimentData.overall_sentiment}
                          </span>
                        </h3>

                        {/* DONUT + LEGEND + DETAILS */}
                        <div className="d-flex align-items-center gap-4">
                          {/* DONUT + LEGEND */}
                          <div className="d-flex align-items-center gap-3">
                            {/* DONUT */}
                            <div
                              style={{
                                width: 150,
                                height: 150,
                                borderRadius: "50%",
                                background: sentimentData.overall_sentiment === "Insufficient evidence"
                                  ? "#374151"
                                  : sentimentData.sentiment_breakdown?.positive_percent && sentimentData.sentiment_breakdown?.neutral_percent !== undefined && sentimentData.sentiment_breakdown?.negative_percent
                                  ? `conic-gradient(
                    #2d5a3d 0deg ${sentimentData.sentiment_breakdown.positive_percent * 3.6}deg,
                    #b8860b ${sentimentData.sentiment_breakdown.positive_percent * 3.6}deg ${(sentimentData.sentiment_breakdown.positive_percent + (sentimentData.sentiment_breakdown.neutral_percent || 0)) * 3.6}deg,
                    #8b2635 ${(sentimentData.sentiment_breakdown.positive_percent + (sentimentData.sentiment_breakdown.neutral_percent || 0)) * 3.6}deg 360deg
                  )`
                                  : "#374151",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                minWidth: 150,
                                minHeight: 150,
                              }}
                            >
                              <div
                                style={{
                                  width: 95,
                                  height: 95,
                                  borderRadius: "50%",
                                  background: "#1c1f2e",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexDirection: "column",
                                }}
                              >
                                {sentimentData.overall_sentiment === "Insufficient evidence" ? (
                                  <>
                                    <div className="text-white mb-0" style={{ fontSize: "12px", fontWeight: 500, textAlign: "center" }}>
                                      Insufficient
                                    </div>
                                    <div className="text-white mb-0" style={{ fontSize: "12px", fontWeight: 500, textAlign: "center" }}>
                                      Evidence
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="text-white mb-0" style={{ fontSize: "22px", fontWeight: 500 }}>
                                      {
                                        sentimentData.sentiment_breakdown?.positive_percent || 0
                                      }
                                      %
                                    </div>
                                    <small className="text-muted">
                                      {sentimentData.overall_sentiment || 'Unknown'}
                                    </small>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* LEGEND */}
                            {sentimentData.overall_sentiment !== "Insufficient evidence" && sentimentData.sentiment_breakdown && (
                              <div className="d-flex flex-column gap-2">
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    style={{
                                      width: 12,
                                      height: 12,
                                      backgroundColor: "#2d5a3d",
                                      borderRadius: 2,
                                    }}
                                  ></div>
                                  <span className="text-white small">
                                    Positive{" "}
                                    {
                                      sentimentData.sentiment_breakdown?.positive_percent || 0
                                    }
                                    %
                                  </span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    style={{
                                      width: 12,
                                      height: 12,
                                      backgroundColor: "#b8860b",
                                      borderRadius: 2,
                                    }}
                                  ></div>
                                  <span className="text-white small">
                                    Neutral{" "}
                                    {
                                      sentimentData.sentiment_breakdown?.neutral_percent || 0
                                    }
                                    %
                                  </span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    style={{
                                      width: 12,
                                      height: 12,
                                      backgroundColor: "#8b2635",
                                      borderRadius: 2,
                                    }}
                                  ></div>
                                  <span className="text-white small">
                                    Negative{" "}
                                    {
                                      sentimentData.sentiment_breakdown?.negative_percent || 0
                                    }
                                    %
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* DETAILS */}
                          <div className="flex-grow-1">
                            {/* STACK BAR WITH PERCENTAGES */}
                            <div
                              className="mb-3 w-100"
                              style={{
                                height: 30,
                                borderRadius: 10,
                                overflow: "hidden",
                                position: "relative",
                                display: "flex",
                              }}
                            >
                              {sentimentData.sentiment_breakdown?.positive_percent > 0 && (
                                <div
                                  style={{
                                    width: `${sentimentData.sentiment_breakdown.positive_percent}%`,
                                    background: "#2d5a3d",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    position: "relative",
                                  }}
                                >
                                  <span
                                    className="text-white small"
                                    style={{ fontWeight: "bold" }}
                                  >
                                    {
                                      sentimentData.sentiment_breakdown.positive_percent
                                    }
                                    %
                                  </span>
                                </div>
                              )}
                              {sentimentData.sentiment_breakdown?.neutral_percent &&
                                sentimentData.sentiment_breakdown.neutral_percent > 0 && (
                                  <div
                                    style={{
                                      width: `${sentimentData.sentiment_breakdown.neutral_percent}%`,
                                      background: "#b8860b",
                                      height: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      position: "relative",
                                    }}
                                  >
                                    <span
                                      className="text-white small"
                                      style={{ fontWeight: "bold" }}
                                    >
                                      {
                                        sentimentData.sentiment_breakdown.neutral_percent
                                      }
                                      %
                                    </span>
                                  </div>
                                )}
                              {sentimentData.sentiment_breakdown?.negative_percent > 0 && (
                                <div
                                  style={{
                                    width: `${sentimentData.sentiment_breakdown.negative_percent}%`,
                                    background: "#8b2635",
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    position: "relative",
                                  }}
                                >
                                  <span
                                    className="text-white small"
                                    style={{ fontWeight: "bold" }}
                                  >
                                    {
                                      sentimentData.sentiment_breakdown.negative_percent
                                    }
                                    %
                                  </span>
                                </div>
                              )}
                              {(!sentimentData.sentiment_breakdown?.positive_percent && !sentimentData.sentiment_breakdown?.neutral_percent && !sentimentData.sentiment_breakdown?.negative_percent) && (
                                <div className="d-flex align-items-center justify-content-center w-100">
                                  <span className="text-muted small">No sentiment data available</span>
                                </div>
                              )}
                            </div>

                            <p className="text-muted mt-3 small">
                              {sentimentData.overall_summary || 'No summary available'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* DATA CONFIDENCE */}
                  <div className="col-lg-3 mb-3">
                    <div
                      className="p-3 rounded-4 h-100"
                      style={{
                        backgroundColor: "rgba(23, 24, 33, 1)",
                        border: "1px solid #3f4150",
                      }}
                    >
                      <h3 className="text-white mb-3" style={{ fontSize: '18px' }}>Data Confidence</h3>

                      <ul className="text-muted small ps-3 mb-4">
                        <li>Review volume consistency</li>
                        <li>Cross app similarity</li>
                        <li>Language clarity</li>
                        <li>Recency of feedback</li>
                      </ul>

                      <div className="text-center">
                        <span className="badge bg-success px-3 py-2">
                          {sentimentData.data_confidence || 'Unknown'} Confidence
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* THEMES ROW */}
                <div className="row">
                  {/* POSITIVE THEMES */}
                  <div className="col-lg-6 mb-4">
                    <div
                      className="p-4 rounded-4"
                      style={{
                        backgroundColor: "rgba(23, 24, 33, 1)",
                        border: "1px solid #3f4150",
                      }}
                    >
                      <h3 className="text-white mb-4 d-flex align-items-center gap-2" style={{ fontSize: '18px' }}>
                        <i className="fas fa-smile text-success me-2"></i>
                        Top Positive Themes
                      </h3>

                      {sentimentData.top_positive_themes && sentimentData.top_positive_themes.length > 0 ? (
                        sentimentData.top_positive_themes.map((theme, i) => (
                          <div key={i} className="mb-4">
                            <h4 className="text-white" style={{ fontSize: '14px' }}>{theme.theme}</h4>

                            <div className="small text-muted mb-2">
                              <i className="fas fa-chart-line me-1"></i>
                              Sentiment Impact: High |{" "}
                              <i className="fas fa-users me-1"></i>Frequency: High
                            </div>

                            {theme.examples?.[0] && (
                              <div className="p-3 rounded bg-black bg-opacity-25 small text-muted">
                                "{theme.examples[0]}"
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4">
                          <i className="fas fa-smile text-muted mb-3" style={{ fontSize: "48px" }}></i>
                          <p className="text-muted">No positive themes data available</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* NEGATIVE THEMES */}
                  <div className="col-lg-6 mb-4">
                    <div
                      className="p-4 rounded-4"
                      style={{
                        backgroundColor: "rgba(23, 24, 33, 1)",
                        border: "1px solid #3f4150",
                      }}
                    >
                      <h3 className="text-white mb-4 d-flex align-items-center gap-2" style={{ fontSize: '18px' }}>
                        <i className="fas fa-frown text-danger me-2"></i>
                        Top Negative Themes
                      </h3>

                      {sentimentData.top_negative_themes && sentimentData.top_negative_themes.length > 0 ? (
                        sentimentData.top_negative_themes.map((theme, i) => (
                          <div key={i} className="mb-4">
                            <h4 className="text-white" style={{ fontSize: '14px' }}>{theme.issue}</h4>

                            <div className="small text-muted mb-2">
                              <i className="fas fa-exclamation-triangle me-1"></i>
                              Sentiment Impact: Very High |{" "}
                              <i className="fas fa-users me-1"></i>Frequency: High
                            </div>

                            {theme.examples?.[0] && (
                              <div className="p-3 rounded bg-black bg-opacity-25 small text-muted">
                                "{theme.examples[0]}"
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4">
                          <i className="fas fa-frown text-muted mb-3" style={{ fontSize: "48px" }}></i>
                          <p className="text-muted">No negative themes data available</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* BUG COMPLAINTS */}
                {sentimentData.bug_complaints &&
                  sentimentData.bug_complaints.length > 0 && (
                    <div className="row">
                      <div className="col-12 mb-4">
                        <div
                          className="p-4 rounded-4"
                          style={{
                            backgroundColor: "rgba(23, 24, 33, 1)",
                            border: "1px solid #3f4150",
                          }}
                        >
                          <h3 className="text-warning mb-4" style={{ fontSize: '18px' }}>
                            Bug & Stability Themes / Complaints
                          </h3>

                          {sentimentData.bug_complaints.map(
                            (complaint, index) => (
                              <div
                                key={index}
                                className="mb-4 pb-3"
                                style={{
                                  borderBottom:
                                    index <
                                      sentimentData.bug_complaints.length - 1
                                      ? "1px solid #3f4150"
                                      : "none",
                                }}
                              >
                                {typeof complaint === "string" ? (
                                  <div>
                                    <h4 className="text-white mb-2" style={{ fontSize: '14px' }}>
                                      Game Issues
                                    </h4>

                                    <div className="row mb-3">
                                      <div className="col-md-3">
                                        <div className="text-center">
                                          <div
                                            className="text-danger"
                                            style={{
                                              fontSize: "12px",
                                              fontWeight: "bold",
                                            }}
                                          >
                                            HIGH
                                          </div>
                                          <div
                                            className="text-muted"
                                            style={{ fontSize: "11px" }}
                                          >
                                            <i className="fas fa-exclamation-circle me-1"></i>
                                            Severity
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-md-3">
                                        <div className="text-center">
                                          <div
                                            className="text-warning"
                                            style={{
                                              fontSize: "12px",
                                              fontWeight: "bold",
                                            }}
                                          >
                                            FREQUENT
                                          </div>
                                          <div
                                            className="text-muted"
                                            style={{ fontSize: "11px" }}
                                          >
                                            <i className="fas fa-redo me-1"></i>
                                            Recurrence
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-md-3">
                                        <div className="text-center">
                                          <div
                                            className="text-warning"
                                            style={{
                                              fontSize: "12px",
                                              fontWeight: "bold",
                                            }}
                                          >
                                            MEDIUM
                                          </div>
                                          <div
                                            className="text-muted"
                                            style={{ fontSize: "11px" }}
                                          >
                                            <i className="fas fa-code me-1"></i>
                                            Tech Debt Risk
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-md-3">
                                        <div className="text-center">
                                          <div
                                            className="text-danger"
                                            style={{
                                              fontSize: "12px",
                                              fontWeight: "bold",
                                            }}
                                          >
                                            HIGH
                                          </div>
                                          <div
                                            className="text-muted"
                                            style={{ fontSize: "11px" }}
                                          >
                                            <i className="fas fa-star me-1"></i>
                                            Store Rating Impact
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="p-3 rounded bg-black bg-opacity-25 small text-muted">
                                      "{complaint}"
                                    </div>
                                  </div>
                                ) : complaint.bug ? (
                                  <div>
                                    <h4 className="text-white mb-2" style={{ fontSize: '14px' }}>
                                      {complaint.bug}
                                    </h4>

                                    <div className="row mb-3">
                                      <div className="col-md-3">
                                        <div className="text-center">
                                          <div
                                            className="text-danger"
                                            style={{
                                              fontSize: "12px",
                                              fontWeight: "bold",
                                            }}
                                          >
                                            HIGH
                                          </div>
                                          <div
                                            className="text-muted"
                                            style={{ fontSize: "11px" }}
                                          >
                                            <i className="fas fa-exclamation-circle me-1"></i>
                                            Severity
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-md-3">
                                        <div className="text-center">
                                          <div
                                            className="text-warning"
                                            style={{
                                              fontSize: "12px",
                                              fontWeight: "bold",
                                            }}
                                          >
                                            FREQUENT
                                          </div>
                                          <div
                                            className="text-muted"
                                            style={{ fontSize: "11px" }}
                                          >
                                            <i className="fas fa-redo me-1"></i>
                                            Recurrence
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-md-3">
                                        <div className="text-center">
                                          <div
                                            className="text-warning"
                                            style={{
                                              fontSize: "12px",
                                              fontWeight: "bold",
                                            }}
                                          >
                                            MEDIUM
                                          </div>
                                          <div
                                            className="text-muted"
                                            style={{ fontSize: "11px" }}
                                          >
                                            <i className="fas fa-code me-1"></i>
                                            Tech Debt Risk
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-md-3">
                                        <div className="text-center">
                                          <div
                                            className="text-danger"
                                            style={{
                                              fontSize: "12px",
                                              fontWeight: "bold",
                                            }}
                                          >
                                            HIGH
                                          </div>
                                          <div
                                            className="text-muted"
                                            style={{ fontSize: "11px" }}
                                          >
                                            <i className="fas fa-star me-1"></i>
                                            Store Rating Impact
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {complaint.examples &&
                                      complaint.examples.length > 0 && (
                                        <div className="p-3 rounded bg-black bg-opacity-25 small text-muted">
                                          "{complaint.examples[0]}"
                                        </div>
                                      )}
                                  </div>
                                ) : null}
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            ) : (
              <div className="text-center py-5">
                <p className="text-muted">No sentiment data available</p>
              </div>
            )
          ) : itemIds.length > 9999 ? (
            <div className="d-flex justify-content-center py-5">
              <Button color="secondary" disabled={true}>
                Buy subscription to Track more
              </Button>
            </div>
          ) : (isGuest || guestTrackAttempted) ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "400px",
                border: "1px solid rgba(255, 255, 255, 0.05)",
                borderRadius: "16px",
                padding: "40px",
                textAlign: "center",
                background: "rgba(23, 24, 33, 0.4)"
              }}
            >
              <GuestRestrictedPlaceholder 
                height="320px"
                subMessage="Sign in to track this app and unlock custom analytics, performance insights, and historical growth data."
              />
            </div>
          ) : (
            <div className="py-5">
              <div className="d-flex justify-content-center">
                <p>Track the app to gain insights into its user sentiment analysis.</p>
              </div>
              <div className="d-flex justify-content-center">
                <Button
                  style={{ backgroundColor: "#F6255A", borderColor: "#F6255A" }}
                  className="col-md-3"
                  disabled={loading || btnLoader || !isAppdetailLoaded}
                  onClick={() => {
                    setBtnLoader(true)
                    trackNewApp(gameID)
                  }}
                >
                  {btnLoader ? (
                    <div className="d-flex justify-content-center">
                      <Spinner size="sm" color="light" />
                    </div>
                  ) : (
                    "Track App"
                  )}
                </Button>
              </div>
            </div>
          )}
          <Reviews appdata={appdetail} />
        </div>
      ),
    },
    {
      label: "Custom Analytics",
      icon: FaChartLine,
      content: (
        <div className="mt-3">
          <p style={{ color: "rgba(255, 255, 255, 0.6)", fontSize: "14px", marginBottom: "20px" }}>
            Custom KeyQuest insights for better app performance tracking and optimization.
          </p>

          {loading || !isAppdetailLoaded ? (
            <div className="py-3">
              <SkeletonParagraph lines={8} />
            </div>
          ) : itemIds.includes(appdetail?.item_id) ? (
            comparisonList
              .filter(app => app.game_details.item_id === appdetail?.item_id)
              .map((app, index) => {
                // ── Metric Data Mapping ──────────────────────────────
                const fmtValue = (v, format) => {
                  if (v === null || v === undefined) return "N/A";
                  switch (format) {
                    case "currency":
                      return v >= 1000000 ? `$${(v / 1000000).toFixed(2)}M` : v >= 1000 ? `$${(v / 1000).toFixed(1)}K` : `$${v.toFixed(0)}`;
                    case "rating":
                      return v.toFixed(2);
                    case "decimal":
                      return formatDecimal(v, 2);
                    default:
                      return formatNumber(v);
                  }
                };

                const getChange = (key) => {
                  const stats = getMetricStats(key);
                  return { pct: stats.changePct || 0 };
                };

                const prepareChartData = (key) => {
                  if (!Array.isArray(appHistoryData)) return [];
                  return appHistoryData.map(entry => {
                    const d = new Date(entry.created_at_date || entry.created_at * 1000);
                    const date = `${String(d.getDate()).padStart(2, '0')}-${d.toLocaleString('default', { month: 'short' })}-${d.getFullYear()}`;
                    let value = entry[key];
                    if (key === 'ktr') value = value * 1000;
                    return { date, value };
                  });
                };

                const analytics = app.game_details.analytics;
                const metricsList = [
                  {
                    id: "asr",
                    title: "App Store Score",
                    description: "Store Ranking is based on many aspects like KU, Downloads, KTR etc.",
                    explanation: "A higher App Store Score means better overall visibility and discoverability. This composite score factors in downloads, keyword rankings, and user engagement. Aim for consistent growth.",
                    format: "decimal",
                    higherIsBetter: true,
                    icon: BarChart3,
                    color: "#F6255A"
                  },
                  {
                    id: "aur",
                    title: "Average User Rating",
                    description: "The average star rating from all user reviews.",
                    explanation: "A rating above 4.5 is excellent for store visibility. Ratings directly impact conversion rates — users are 3× more likely to install apps rated 4.5+. Address negative feedback quickly to maintain high ratings.",
                    format: "rating",
                    higherIsBetter: true,
                    icon: Star,
                    color: "#f59e0b"
                  },
                  {
                    id: "ktr",
                    title: "Keyword Relevance",
                    description: "Keyword Relevance measures how closely the app's title, description, and metadata match the keywords users are searching for.",
                    explanation: "Higher Keyword Relevance means your app listing is well-optimized for the terms users actually search. A score above 10,000 is strong. Drops indicate metadata misalignment with trending search terms.",
                    format: "decimal",
                    higherIsBetter: true,
                    icon: Search,
                    color: "#3b82f6"
                  },
                  {
                    id: "ku",
                    title: "Keyword Usage",
                    description: "Keyword Usage measures how well an app utilizes high-ranking or relevant keywords in its metadata (title, description, and tags).",
                    explanation: "Higher Keyword Usage means you're effectively leveraging important keywords in your listing. A score of 80+ is excellent. Track this to ensure metadata updates don't accidentally drop key terms.",
                    format: "number",
                    higherIsBetter: true,
                    icon: Search,
                    color: "#8b5cf6"
                  },
                  {
                    id: "d",
                    title: "Downloads",
                    description: "Total cumulative downloads of the app across all platforms.",
                    explanation: "More downloads means wider adoption and better organic ranking. Consistent download growth signals healthy market demand. Sudden drops may indicate increased competition or ASO issues.",
                    format: "number",
                    higherIsBetter: true,
                    icon: Download,
                    color: "#10b981"
                  },
                  {
                    id: "urv",
                    title: "User Reviews Volume",
                    description: "Total number of user reviews submitted for the app.",
                    explanation: "More reviews improve social proof and store ranking. A healthy app should see steady review growth. Monitor review velocity — a sudden drop could indicate engagement issues.",
                    format: "number",
                    higherIsBetter: true,
                    icon: MessageSquare,
                    color: "#6366f1"
                  },
                  {
                    id: "revenue",
                    title: "Total Revenue",
                    description: "Cumulative revenue generated from app sales and in-app purchases.",
                    explanation: "Revenue growth indicates healthy monetization. Compare with download trends — if downloads grow but revenue stalls, your conversion funnel may need optimization. Sudden dips may signal pricing or market issues.",
                    format: "currency",
                    higherIsBetter: true,
                    icon: DollarSign,
                    color: "#ec4899"
                  },
                ].map(m => {
                  let val = Number(analytics?.[m.id]) || 0;
                  if (m.id === 'ktr') val = val * 1000;
                  return {
                    ...m,
                    currentValue: val,
                    pct: getChange(m.id).pct,
                    data: prepareChartData(m.id)
                  };
                });

                // Logic for summary bar (Top 4)
                const summaryMetrics = metricsList.slice(0, 4);

                return (
                  <div key={index}>
                    {/* Expanded Chart Modal */}
                    <ExpandedChartDialog
                      metric={expandedChartMetric}
                      open={!!expandedChartMetric}
                      onClose={() => setExpandedChartMetric(null)}
                      fmtValue={fmtValue}
                    />

                    {/* Top Summary Bar */}
                    {/* <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4 }}
                      className="d-flex flex-wrap align-items-center gap-4 mb-4 pb-3"
                      style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.05)" }}
                    >
                      {summaryMetrics.map((m) => (
                        <div key={m.title} className="d-flex align-items-center gap-3">
                          <div style={{
                            height: "32px",
                            width: "32px",
                            borderRadius: "8px",
                            backgroundColor: "rgba(246, 37, 90, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}>
                            <m.icon size={16} style={{ color: "#F6255A" }} />
                          </div>
                          <div>
                            <p style={{
                              fontSize: "10px",
                              color: "rgba(255, 255, 255, 0.5)",
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                              margin: 0
                            }}>
                              {m.title}
                            </p>
                            <p style={{
                              fontSize: "16px",
                              fontWeight: "bold",
                              fontFamily: "monospace",
                              color: "white",
                              margin: 0
                            }}>
                              {fmtValue(m.currentValue, m.format)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </motion.div> */}

                    {/* Main Content Grid */}
                    <div className="row g-4">
                      {/* Sidebar Summary */}
                      <div className="col-lg-3 order-2 order-lg-1" style={{ alignSelf: "flex-start", position: "sticky", top: "110px", zIndex: 10 }}>
                        <div style={{
                          backgroundColor: "rgba(23, 24, 33, 0.4)",
                          border: "1px solid rgba(255, 255, 255, 0.05)",
                          borderRadius: "12px",
                          padding: "12px",
                        }}>
                          <h2 style={{
                            fontSize: "10px",
                            textTransform: "uppercase",
                            letterSpacing: "0.2em",
                            color: "rgba(255, 255, 255, 0.4)",
                            fontWeight: "600",
                            padding: "4px 12px 12px 12px",
                            margin: 0
                          }}>
                            Store Analytics
                          </h2>
                          <div className="d-flex flex-column gap-1">
                            {metricsList.map((m) => (
                              <MetricSummaryRow
                                key={m.id}
                                metric={m}
                                onClick={() => setExpandedChartMetric(m)}
                                fmtValue={fmtValue}
                              />
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Charts Grid */}
                      <div className="col-lg-9 order-1 order-lg-2">
                        <div className="row g-4">
                          {metricsList.map((m) => {
                            const isRestricted = requiresUpgrade(analytics, m.id);

                            return (
                              <div key={m.id} className="col-md-6" style={{ position: 'relative' }}>
                                <div style={{
                                  filter: isRestricted ? 'blur(8px)' : 'none',
                                  pointerEvents: isRestricted ? 'none' : 'auto',
                                  height: '100%'
                                }}>
                                  <MetricChartSection
                                    metric={m}
                                    onExpand={() => setExpandedChartMetric(m)}
                                    fmtValue={fmtValue}
                                  />
                                </div>
                                {isRestricted && (
                                  <div
                                    onClick={() => setShowPlanModal(true)}
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'pointer',
                                      zIndex: 10,
                                      background: 'rgba(23, 24, 33, 0.4)',
                                      borderRadius: '12px',
                                      border: '1px solid rgba(255, 255, 255, 0.1)'
                                    }}
                                  >
                                    <div style={{
                                      width: '40px',
                                      height: '40px',
                                      borderRadius: '50%',
                                      backgroundColor: 'rgba(246, 37, 90, 0.2)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      marginBottom: '10px'
                                    }}>
                                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F6255A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                      </svg>
                                    </div>
                                    <p style={{ color: 'white', fontSize: '12px', fontWeight: '500', margin: 0 }}>Unlock {m.title}</p>
                                    <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '10px', margin: '4px 0 0 0' }}>with KeyQuest Premium</p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Timestamp Footer */}
                    <div className="mt-4 pt-3 d-flex justify-content-between align-items-center" style={{ borderTop: "1px solid rgba(255, 255, 255, 0.05)" }}>
                      <div style={{
                        fontSize: "12px",
                        color: "rgba(255, 255, 255, 0.4)",
                        fontStyle: "italic"
                      }}>
                        Updated on {new Date(app.game_details?.create_time * 1000 || new Date()).toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              })
          ) : itemIds.length > 9999 ? (
            <Button color="secondary" disabled="true">
              Buy subscription to Track more
            </Button>
          ) : (isGuest || guestTrackAttempted) ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "400px",
                border: "1px solid rgba(255, 255, 255, 0.05)",
                borderRadius: "16px",
                padding: "40px",
                textAlign: "center",
                background: "rgba(23, 24, 33, 0.4)"
              }}
            >
              <GuestRestrictedPlaceholder 
                height="320px"
                subMessage="Sign in to track this app and unlock custom analytics, performance insights, and historical growth data."
              />
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "400px",
                border: "2px dashed rgba(255, 255, 255, 0.05)",
                borderRadius: "16px",
                padding: "40px",
                textAlign: "center"
              }}
            >
              <div style={{
                height: "64px",
                width: "64px",
                borderRadius: "50%",
                backgroundColor: "rgba(255, 255, 255, 0.03)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "24px"
              }}>
                <Info size={32} style={{ color: "rgba(255, 255, 255, 0.2)" }} />
              </div>
              <h2 style={{ fontSize: "20px", fontWeight: "600", color: "white", marginBottom: "12px" }}>Not Tracked</h2>
              <p style={{ fontSize: "14px", color: "rgba(255, 255, 255, 0.5)", maxWidth: "340px", marginBottom: "32px", lineHeight: "1.6" }}>
                You're not tracking this app yet. Track it to unlock custom analytics, performance insights, and historical growth data.
              </p>
              <Button
                style={{
                  backgroundColor: "#F6255A",
                  borderColor: "#F6255A",
                  padding: "10px 32px",
                  borderRadius: "8px",
                  fontWeight: "600"
                }}
                disabled={loading || btnLoader || !isAppdetailLoaded}
                onClick={() => {
                  setBtnLoader(true)
                  trackNewApp(gameID)
                }}
              >
                {btnLoader ? <Spinner size="sm" color="light" /> : "Track App Now"}
              </Button>
            </div>
          )}
        </div>
      ),
    },

    {
      label: "Policy Checker",
      icon: FaRegListAlt,
      content: (
        <div className="mt-3">
          {loading || !isAppdetailLoaded || policyLoading ? (
            <div className="py-3">
              <SkeletonParagraph lines={8} />
            </div>
          ) : itemIds.includes(appdetail?.item_id) ? (
            <div className="container-fluid">
              {/* HEADER */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h2 className="text-white mb-1" style={{ fontSize: '22px' }}>App Policy Compliance</h2>
                  <p className="text-muted small mb-0">
                    Review your app's compliance with platform policies and safety guidelines.
                  </p>
                </div>
                <div className="text-end">
                  {policyData?.summary?.compliance_status && (
                    <span className={`badge ${policyData.summary.compliance_status === 'compliant' ? 'bg-success' :
                      policyData.summary.compliance_status === 'non_compliant' ? 'bg-danger' : 'bg-warning'
                      } me-2`}>
                      {policyData.summary.compliance_status === 'compliant' ? 'Compliant' :
                        policyData.summary.compliance_status === 'non_compliant' ? 'Non-Compliant' : 'Needs Review'}
                    </span>
                  )}
                  {policyData?.summary?.overall_risk && (
                    <span className={`badge ${policyData.summary.overall_risk === 'low' ? 'bg-success' :
                      policyData.summary.overall_risk === 'medium' ? 'bg-warning' : 'bg-danger'
                      }`}>
                      Risk: {policyData.summary.overall_risk}
                    </span>
                  )}
                </div>
              </div>

              {/* APP INFO */}
              {/* {policyData?.app && (
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="p-4 rounded-4" style={{ backgroundColor: 'rgba(23, 24, 33, 1)', border: '1px solid #3f4150' }}>
                      <h5 className="text-white mb-4">
                        <i className="fas fa-info-circle me-2"></i>
                        App Information
                      </h5>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <div className="small text-muted mb-1">App Name</div>
                          <div className="text-white">{policyData.app.name || 'N/A'}</div>
                        </div>
                        <div className="col-md-6 mb-3">
                          <div className="small text-muted mb-1">Platform</div>
                          <div className="text-white">{policyData.app.platform || 'N/A'}</div>
                        </div>
                        <div className="col-md-6 mb-3">
                          <div className="small text-muted mb-1">Publisher</div>
                          <div className="text-white">{policyData.app.publisher || 'N/A'}</div>
                        </div>
                        <div className="col-md-6 mb-3">
                          <div className="small text-muted mb-1">Developer</div>
                          <div className="text-white">{policyData.app.developer || 'N/A'}</div>
                        </div>
                        <div className="col-md-6 mb-3">
                          <div className="small text-muted mb-1">Category</div>
                          <div className="text-white">{policyData.app.category || 'N/A'}</div>
                        </div>
                        <div className="col-md-6 mb-3">
                          <div className="small text-muted mb-1">Genre</div>
                          <div className="text-white">
                            {policyData.app.genre && Array.isArray(policyData.app.genre) 
                              ? policyData.app.genre.join(', ') 
                              : (policyData.app.genre || 'N/A')}
                          </div>
                        </div>
                        <div className="col-md-6 mb-3">
                          <div className="small text-muted mb-1">Price</div>
                          <div className="text-white">{policyData.app.price || 'N/A'}</div>
                        </div>
                        <div className="col-md-6 mb-3">
                          <div className="small text-muted mb-1">Free App</div>
                          <div className="text-white">{policyData.app.is_free ? 'Yes' : 'No'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )} */}

              {/* BLOCKING ISSUES */}
              {policyData?.blocking_issues && Array.isArray(policyData.blocking_issues) && policyData.blocking_issues.length > 0 && (
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="p-4 rounded-4" style={{ backgroundColor: 'rgba(23, 24, 33, 1)', border: '1px solid #3f4150' }}>
                      <h3 className="text-white mb-4" style={{ fontSize: '18px' }}>
                        <i className="fas fa-exclamation-triangle me-2 text-danger"></i>
                        Blocking Issues
                      </h3>
                      {policyData.blocking_issues.map((issue, index) => (
                        <div key={index} className="alert alert-danger" role="alert">
                          <h4 className="alert-heading" style={{ fontSize: '14px' }}>{issue.title || 'Unknown Issue'}</h4>
                          <p className="mb-1">{issue.description || 'No description available'}</p>
                          {issue.suggested_improvement && (
                            <>
                              <hr />
                              <p className="mb-0"><strong>How to fix:</strong> {issue.suggested_improvement}</p>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* RECOMMENDED IMPROVEMENTS */}
              {policyData?.recommended_improvements && Array.isArray(policyData.recommended_improvements) && policyData.recommended_improvements.length > 0 && (
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="p-4 rounded-4" style={{ backgroundColor: 'rgba(23, 24, 33, 1)', border: '1px solid #3f4150' }}>
                      <h3 className="text-white mb-4" style={{ fontSize: '18px' }}>
                        <i className="fas fa-lightbulb me-2"></i>
                        Recommended Improvements
                      </h3>
                      {policyData.recommended_improvements.map((improvement, index) => (
                        <div key={index} className="p-3 rounded bg-black bg-opacity-25 mb-3">
                          <h4 className="text-white mb-2" style={{ fontSize: '14px' }}>{improvement.title || 'Untitled Improvement'}</h4>
                          {improvement.policy_area && (
                            <div className="small text-muted mb-2">Policy Area: {improvement.policy_area}</div>
                          )}
                          <p className="text-muted small mb-2">{improvement.description || 'No description available'}</p>
                          {improvement.suggested_improvement && (
                            <p className="text-white small mb-0">
                              <strong>Suggested improvement:</strong> {improvement.suggested_improvement}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* STORE PAGE GAPS */}
              {policyData?.store_page_gaps && Array.isArray(policyData.store_page_gaps) && policyData.store_page_gaps.length > 0 && (
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="p-4 rounded-4" style={{ backgroundColor: 'rgba(23, 24, 33, 1)', border: '1px solid #3f4150' }}>
                      <h3 className="text-white mb-4" style={{ fontSize: '18px' }}>
                        <i className="fas fa-store me-2"></i>
                        Store Page Gaps
                      </h3>
                      {policyData.store_page_gaps.map((gap, index) => (
                        <div key={index} className="p-3 rounded bg-black bg-opacity-25 mb-3">
                          <h4 className="text-white mb-2" style={{ fontSize: '14px' }}>{gap.field || 'Unknown Field'}</h4>
                          <p className="text-muted small mb-2">{gap.issue || 'No issue description available'}</p>
                          {gap.how_to_fix && (
                            <p className="text-white small mb-0">
                              <strong>How to fix:</strong> {gap.how_to_fix}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* SUBMISSION CHECKLIST */}
              {policyData?.submission_checklist && typeof policyData.submission_checklist === 'object' && (
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="p-4 rounded-4" style={{ backgroundColor: 'rgba(23, 24, 33, 1)', border: '1px solid #3f4150' }}>
                      <h3 className="text-white mb-4" style={{ fontSize: '18px' }}>
                        <i className="fas fa-clipboard-check me-2"></i>
                        Submission Checklist
                      </h3>
                      <div className="row">
                        {Object.entries(policyData.submission_checklist).map(([key, value]) => (
                          <div key={key} className="col-md-4 mb-3">
                            <div className="d-flex align-items-center">
                              <i className={`fas ${value === 'provided' ? 'fa-check-circle text-success' : 'fa-times-circle text-danger'
                                } me-2`}></i>
                              <span className="text-white small">
                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </span>
                              {value === 'missing' && (
                                <span className="badge bg-warning ms-2 small">Missing</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* LAST UPDATED */}
              <div className="text-center mt-4">
                <small className="text-muted">
                  Last policy check: {new Date().toLocaleDateString()}
                  {policyData?.summary?.confidence && (
                    <span className="ms-2">Confidence: {Math.round((policyData.summary.confidence || 0) * 100)}%</span>
                  )}
                </small>
              </div>
            </div>
          ) : itemIds.length > 9999 ? (
            <div className="d-flex justify-content-center py-5">
              <Button color="secondary" disabled={true}>
                Buy subscription to Track more
              </Button>
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-center mt-4">
                <p>Track the app to gain insights into its policy compliance.</p>
              </div>
              <div className="d-flex justify-content-center">
                {(isGuest || guestTrackAttempted) ? (
                  <div style={{ width: '100%', maxWidth: '600px' }}>
                    <GuestRestrictedPlaceholder 
                      height="200px"
                      subMessage="Sign in to track apps and access detailed policy compliance reports."
                    />
                  </div>
                ) : (
                  <Button
                    style={{ backgroundColor: "#F6255A", borderColor: "#F6255A" }}
                    className="col-md-3"
                    disabled={loading || btnLoader || !isAppdetailLoaded}
                    onClick={() => {
                      setBtnLoader(true)
                      trackNewApp(gameID)
                    }}
                  >
                    {btnLoader ? (
                      <div className="d-flex justify-content-center">
                        <Spinner size="sm" color="light" />
                      </div>
                    ) : (
                      "Track App"
                    )}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      ),
    },
  ]

  // Early return for 404 Not Found
  if (notFound && !loading) {
    return (
      <>
        <SEO title="Page Not Found" description="The requested app could not be found." robots="noindex, follow" />
        <NotFound
          message="The app you're looking for doesn't exist or has been removed."
          linkText="Back to Dashboard"
          linkTo="/"
        />
      </>
    )
  }

  const seoCanonicalPath = getAppDetailsLink(appdetail)
  const seoTitle = appdetail?.name ? `${appdetail.name} - Downloads, Revenue and more` : "App Details"
  const seoGenreDisplay = getGenreDisplay(appdetail?.genre)
  const seoPublisherName = appdetail?.publisher || appdetail?.developer
  // Use this game's actual platform when known (Meta Quest/Steam/Apple Vision
  // Pro/etc.) rather than assuming Meta Quest for every game - the catalog
  // spans multiple platforms now, so a hardcoded platform name would be
  // wrong for anything not on Meta Quest.
  const seoPlatformName = (() => {
    const p = (appdetail?.platform || "").toLowerCase()
    if (p === "meta" || p === "oculus" || p === "quest") return "Meta Quest"
    if (p === "apple_vision" || p === "apple vision" || p === "vision pro" || p === "visionos") return "Apple Vision Pro"
    if (p === "steam") return "Steam"
    if (appdetail?.platform) return appdetail.platform.charAt(0).toUpperCase() + appdetail.platform.slice(1)
    return "VR and gaming platforms"
  })()
  const seoOverviewText = `${appdetail?.name || "This app"} is a VR ${seoGenreDisplay !== "N/A" ? `${seoGenreDisplay} ` : ""}title on ${seoPlatformName}${seoPublisherName ? `, published by ${seoPublisherName}` : ""}. Track ${appdetail?.name || "this app"}'s download trends, revenue estimates, keyword rankings, and player sentiment on KeyQuest's VR analytics platform to see how it's performing on ${seoPlatformName}.`
  // Meta/og/twitter descriptions have a practical ~155-160 char limit before
  // Google/social platforms truncate them, so this is a short, self-contained
  // summary rather than a cut-off version of the fuller Overview sentence.
  const seoDescription = truncateText(
    `${appdetail?.name || "This app"} - VR ${seoGenreDisplay !== "N/A" ? `${seoGenreDisplay} ` : ""}game for ${seoPlatformName}${seoPublisherName ? ` by ${seoPublisherName}` : ""}. Track downloads, revenue & rankings on KeyQuest.`,
    160
  )
  const seoKeywords = [appdetail?.name, appdetail?.genre, appdetail?.publisher || appdetail?.developer, "downloads", "revenue", "VR app", "VR analytics"]
    .filter(Boolean)
    .join(", ") || generateKeywords(appdetail?.description)
  const seoImage = generateOGImage(appdetail?.image_url)
  const seoStructuredData = appdetail?.name ? {
    "@context": "https://schema.org",
    "@type": "VideoGame",
    name: appdetail.name,
    description: seoDescription,
    image: seoImage,
    url: `${SITE_CONFIG.siteUrl}${seoCanonicalPath}`,
    applicationCategory: "GameApplication",
    ...(appdetail?.platform && { operatingSystem: seoPlatformName }),
    ...(appdetail?.genre && { genre: appdetail.genre }),
    ...((appdetail?.publisher || appdetail?.developer) && {
      author: {
        "@type": "Organization",
        name: appdetail.publisher || appdetail.developer,
      },
    }),
    ...(appdetail?.price?.amount !== undefined && appdetail?.price?.amount !== null && {
      offers: {
        "@type": "Offer",
        price: appdetail.price.amount,
        priceCurrency: appdetail.price.currency || "USD",
      },
    }),
  } : undefined

  return (
    <React.Fragment>
      <SEO
        title={seoTitle}
        appendSiteName={false}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={`${SITE_CONFIG.siteUrl}${seoCanonicalPath}`}
        robots="index, follow"
        author={appdetail?.publisher || appdetail?.developer}
        image={seoImage}
        structuredData={seoStructuredData}
      />
      <style>
        {`
          .revenue-tooltip-custom {
            background-color: #111827 !important;
            color: #F9FAFB !important;
            border: 1px solid #374151 !important;
            border-radius: 8px !important;
            padding: 8px 12px !important;
            font-size: 12px !important;
            font-weight: 400 !important;
            max-width: 300px !important;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
          }
          .revenue-tooltip-custom .tooltip-inner {
            background-color: #111827 !important;
            color: #F9FAFB !important;
            border: 1px solid #374151 !important;
            border-radius: 8px !important;
          }
        `}
      </style>
      <style>{`
        /* Force all ancestors of the sticky sidebar to allow sticky positioning */
        .main-content, #layout-wrapper, body, html {
          overflow: visible !important;
          overflow-x: visible !important;
          overflow-y: visible !important;
        }
      `}</style>
      {showForecastingSeparately ? (
        <div className="page-content" style={{
          minHeight: '100vh',
          backgroundColor: '#171821',
          // Do not use shorthand `padding` here: it would clobber the
          // `.page-content` class's `padding-top` (reserved for the fixed
          // topbar) and let this view render underneath the header.
          paddingLeft: '32px',
          paddingRight: '32px',
          paddingBottom: '32px'
        }}>
          <Forecasting
            onBack={() => {
              setShowForecastingSeparately(false)
              setActiveTab(0)
            }}
            onUpgradeRequired={() => {
              setShowForecastingSeparately(false)
              setShowPlanModal(true)
            }}
            appName={appdetail?.name}
          />
        </div>
      ) : showAICMOSeparately ? (
        <AppScanner
          onBack={() => {
            setShowAICMOSeparately(false)
            setActiveTab(0)
          }}
          appName={appdetail?.name}
          genre={appdetail?.genre?.[0]}
        />
      ) : showSocialComparisonSeparately ? (
        <SocialMediaComparison
          onBack={() => {
            setShowSocialComparisonSeparately(false)
            setActiveTab(0)
          }}
          appId={gameID}
          appName={appdetail?.name}
          imageUrl={appdetail?.image_url}
        />
      ) : showAIReportSeparately ? (
        /* AI Report Page View */
        <div className="page-content" style={{
          minHeight: '100vh',
          backgroundColor: '#171821',
          // See note above: keep `padding-top` reserved for the fixed topbar
          // by not overriding it via the shorthand `padding` property.
          paddingLeft: '32px',
          paddingRight: '32px',
          paddingBottom: '32px'
        }}>
          {/* Report Header Content — the only header/back-button for this view.
              (There used to be a second, generic "AI Reports" header + Go Back
              rendered above this one — pure redundant chrome duplicating what's
              below, removed.) */}
          <div className="mb-4 mt-2">
            {/* Row 1: Title and Go Back */}
            <div className="d-flex justify-content-between align-items-center mb-0">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <motion.div
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 3, repeat: Infinity }}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <Activity size={20} color="#F6255A" />
                    <span style={{ fontSize: '12px', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '2px', color: '#F6255A' }}>
                      AI Performance Report
                    </span>
                  </motion.div>
                </div>
                <h1 style={{ fontSize: '32px', fontWeight: 'bold', margin: 0, letterSpacing: '-0.5px' }}>
                  {appdetail?.name ? `${appdetail.name} - AI Analysis` : 'AI Performance Report'}
                </h1>
              </motion.div>

              <BackButton
                onClick={() => {
                  setShowAIReportSeparately(false)
                  setActiveTab(0)
                }}
              />
            </div>

            {/* Row 2: Dates and Actions */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginTop: '16px', width: '100%' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Calendar size={14} color="#a0a0a0" style={{ marginRight: '4px' }} />
                <DatePicker
                  selected={aiStartDate ? new Date(aiStartDate.replace(/-/g, '/')) : null}
                  onChange={date => {
                    if (date) {
                      const y = date.getFullYear();
                      const m = String(date.getMonth() + 1).padStart(2, '0');
                      const d = String(date.getDate()).padStart(2, '0');
                      setAiStartDate(`${y}-${m}-${d}`);
                    }
                  }}
                  dateFormat="MMM d"
                  className="custom-ai-datepicker-minimal"
                  disabled={loading || !isAppdetailLoaded}
                />
                <span style={{ opacity: 0.5, color: '#a0a0a0' }}>–</span>
                <DatePicker
                  selected={aiEndDate ? new Date(aiEndDate.replace(/-/g, '/')) : null}
                  onChange={date => {
                    if (date) {
                      const y = date.getFullYear();
                      const m = String(date.getMonth() + 1).padStart(2, '0');
                      const d = String(date.getDate()).padStart(2, '0');
                      setAiEndDate(`${y}-${m}-${d}`);
                    }
                  }}
                  dateFormat="MMM d"
                  className="custom-ai-datepicker-minimal"
                  disabled={loading || !isAppdetailLoaded}
                />
                <span style={{ color: '#fff', fontWeight: '600', fontSize: '14px', marginLeft: '-2px' }}>, {aiEndDate ? new Date(aiEndDate.replace(/-/g, '/')).getFullYear() : new Date().getFullYear()}</span>
                <span style={{ marginLeft: '8px', color: '#a0a0a0', fontWeight: 'normal' }}>· {getAiRangeSummary()}</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={loading || aiGenerating || aiCacheLoading || !isAppdetailLoaded}
                  onClick={async () => {
                    await fetchAppinfo(aiStartDate, aiEndDate, "true", true)
                  }}
                  style={{
                    backgroundColor: '#F6255A',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: (loading || aiGenerating || aiCacheLoading || !isAppdetailLoaded) ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 12px rgba(246, 37, 90, 0.2)',
                    transition: 'all 0.3s ease',
                    opacity: (loading || aiGenerating || aiCacheLoading || !isAppdetailLoaded) ? 0.7 : 1
                  }}
                >
                  {aiGenerating ? <Spinner size="sm" color="light" /> : <><Zap size={14} /> Generate</>}
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02, backgroundColor: 'rgba(255, 255, 255, 0.15)' }}
                  whileTap={{ scale: 0.98 }}
                  disabled={loading || !appInfo || aiExporting || !isAppdetailLoaded}
                  onClick={exportAiReportPdf}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: (loading || !appInfo || aiExporting || !isAppdetailLoaded) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: (loading || !appInfo || aiExporting || !isAppdetailLoaded) ? 0.5 : 1
                  }}
                >
                  {aiExporting ? <Spinner size="sm" color="light" /> : <><Download size={14} /> Export</>}
                </motion.button>
              </div>
            </motion.div>
          </div>

          <div className="key_track_sec" style={{ height: "auto", position: "relative" }}>


            {/* AI Report Content */}
            {(aiGenerating || aiCacheLoading) ? (
              <AIReportSkeleton />
            ) : (
              <AIReportDisplay reportData={appInfo} appName={appdetail?.name} onGoBack={() => {
                setShowAIReportSeparately(false)
                setActiveTab(0) // Go to About tab (index 0)
              }} />
            )}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              style={{
                marginTop: 32,  // space from the section above
                marginBottom: 16,
              }}
            >
              {/* Header with icon */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  marginBottom: 12,
                }}
              >
                <Zap style={{ width: 20, height: 20, color: "#F6255A" }} />
                <span
                  style={{
                    fontSize: 14,
                    fontFamily: "JetBrains Mono, monospace",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: "#F6255A",
                  }}
                >
                  KeyQuest AI
                </span>
              </div>

              {/* Ask AI query input + button */}
              <div style={{ display: "flex", gap: 8 }}>
                <textarea
                  rows={1}
                  placeholder="Ask any question about this report from KeyQuest AI..."
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && aiQuery?.trim().length > 0) {
                      e.preventDefault();
                      setAskOpen(true);
                    }
                  }}
                  style={{
                    flex: 1,
                    resize: "vertical",
                    minHeight: 44,
                    padding: "12px 12px",
                    fontSize: 14,
                    lineHeight: 1.4,
                    borderRadius: 12,
                    backgroundColor: isFocused ? "rgba(34,36,50,1)" : "rgba(23,24,33,1)",
                    border: isFocused ? "1px solid #F6255A" : "none",
                    outline: "none",
                    color: "#FFFFFF",
                    transition: "border-color 0.2s, background-color 0.2s",
                  }}
                />

                <Button
                  color="primary"
                  disabled={!aiQuery || aiQuery.trim().length === 0}
                  onClick={() => setAskOpen(true)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "0 12px",
                    height: 44,
                    backgroundColor: "#F6255A",
                    borderColor: "#F6255A",
                    cursor: !aiQuery ? "not-allowed" : "pointer",
                    opacity: !aiQuery ? 0.6 : 1,
                  }}
                >
                  <Send style={{ width: 16, height: 16 }} />
                  Ask
                </Button>
              </div>
            </motion.div>

            {/* Chat modal for Ask AI */}
            <ChatbotUI
              toggle={askOpen}
              togglemodal={toggleAsk}
              initialPrompt={aiQuery}
              pageDescription={`Ask AI about ${appdetail?.name || 'this app'}`}
              aiReport={appInfo ? (typeof appInfo === "string" ? appInfo : JSON.stringify(appInfo)) : ''}
            />
          </div>
        </div>
      ) : (
        /* Normal Dashboard Content */
        <div
          className="container-fluid"
          style={{ maxWidth: "100vw", padding: 32, overflow: "visible" }}
        >
          <div className="category_track_innersec" style={{ overflow: "visible" }}>
            <div className="row">
              <div className="col-sm-12">
                <div className="d-flex align-items-center justify-content-between mb-0">
                  <span
                    style={{
                      fontFamily: "Rubik",
                      fontWeight: 600,
                      fontSize: "32px",
                      color: "#fff",
                    }}
                  >
                    App Store Listing
                  </span>
                </div>
              </div>
            </div>
            <div className="key_track_sec" style={{ height: "auto", position: "relative", overflow: "visible" }}>
              {/* TOP LEFT: Go Back Button */}
              <div className="go-back-btn-top-wrapper" style={{ position: "absolute", top: "20px", left: "20px", zIndex: 10 }}>
                <BackButton
                  className="go-back-btn-top"
                  onClick={handleGoBack}
                  disabled={loading || !isAppdetailLoaded}
                />
              </div>

              {/* TOP RIGHT: Track and Tracking Buttons - All in one row */}
              <div className="top-right-tracking-buttons desk_on_track" style={{ position: "absolute", top: "20px", right: "20px", zIndex: 10, display: "flex", flexDirection: "row", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                {/* Track Button - First */}
                {!itemIds.includes(appdetail?.item_id) && (
                  <button
                    type="button"
                    value="Track"
                    disabled={loading || btnLoader || !isAppdetailLoaded}
                    onClick={() => {
                      setBtnLoader(true)
                      trackNewApp(appdetail?.item_id)
                    }}
                    style={{
                      padding: "8px 16px",
                      borderRadius: 8,
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: "13px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      backgroundColor: "#FF4B7D",
                      color: "#ffffff",
                      border: "none",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                      transition: "background-color 0.15s ease, transform 0.1s ease",
                      width: 'auto',
                      minWidth: 'fit-content',
                      opacity: loading || btnLoader || !isAppdetailLoaded ? 0.5 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!loading && !btnLoader && isAppdetailLoaded) {
                        e.currentTarget.style.backgroundColor = '#ff627d'
                        e.currentTarget.style.transform = 'translateY(-1px)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#FF4B7D'
                      e.currentTarget.style.transform = 'translateY(0)'
                    }}
                  >
                    {btnLoader ? "Loading..." : "Track"}
                  </button>
                )}
                {/* Track Keywords Button - Second */}
                <button
                  type="button"
                  disabled={loading || btnLoader || !isAppdetailLoaded}
                  onClick={handleKeywordTracking}
                  style={{
                    ...trackingActionButtonStyle,
                    width: 'auto',
                    minWidth: 'fit-content',
                    marginLeft: 0,
                    opacity: loading || btnLoader || !isAppdetailLoaded ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!loading && !btnLoader && isAppdetailLoaded) {
                      const btn = e.currentTarget
                      btn.style.backgroundColor = '#3a3a48'
                      btn.style.borderColor = '#505062'
                      btn.style.transform = 'translateY(-1px)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    const btn = e.currentTarget
                    btn.style.backgroundColor = trackingActionButtonStyle.backgroundColor
                    btn.style.borderColor = trackingActionButtonStyle.border.split(' ')[2]
                    btn.style.color = trackingActionButtonStyle.color
                    btn.style.transform = 'translateY(0)'
                  }}
                >
                  {itemIds.includes(appdetail?.item_id) ? 'Show Keywords for this App' : 'Track Keywords for this App'}
                </button>
                {/* Track Categories Button - Third */}
                <button
                  type="button"
                  disabled={loading || btnLoader || !isAppdetailLoaded}
                  onClick={handleCategoryTracking}
                  style={{
                    ...trackingActionButtonStyle,
                    width: 'auto',
                    minWidth: 'fit-content',
                    opacity: loading || btnLoader || !isAppdetailLoaded ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!loading && !btnLoader && isAppdetailLoaded) {
                      const btn = e.currentTarget
                      btn.style.backgroundColor = '#3a3a48'
                      btn.style.borderColor = '#505062'
                      btn.style.transform = 'translateY(-1px)'
                    }
                  }}
                  onMouseLeave={(e) => {
                    const btn = e.currentTarget
                    btn.style.backgroundColor = trackingActionButtonStyle.backgroundColor
                    btn.style.borderColor = trackingActionButtonStyle.border.split(' ')[2]
                    btn.style.color = trackingActionButtonStyle.color
                    btn.style.transform = 'translateY(0)'
                  }}
                >
                  {itemIds.includes(appdetail?.item_id) ? 'Show Categories for this App' : 'Track Categories for this App'}
                </button>
              </div>

              {loading ? (
                <>
                  <div className="app_dashboard_top" style={{ marginTop: '60px' }}>
                    <div className="app_dashboard_lt">
                      <div className="app_dashboard_lt_inn_img">
                        <div className="placeholder-glow">
                          <span
                            className="placeholder"
                            style={{
                              width: "80px",
                              height: "80px",
                              borderRadius: "8px",
                            }}
                          ></span>
                        </div>
                      </div>
                      <div className="app_dashboard_lt_inn_txt">
                        <div className="placeholder-glow">
                          <span className="placeholder col-6"></span>
                        </div>
                        <div className="placeholder-glow">
                          <span className="placeholder col-4"></span>
                        </div>
                        <div className="placeholder-glow">
                          <span className="placeholder col-3"></span>
                        </div>
                      </div>
                    </div>
                    <div className="app_dashboard_rt desk_on_track">
                      <div className="placeholder-glow">
                        <span className="placeholder col-2"></span>
                      </div>
                      <div className="placeholder-glow">
                        <span className="placeholder col-4"></span>
                      </div>
                    </div>
                  </div>
                  <div className="app_dashboard_mid">
                    <div className="app_dashboard_mid_content">
                      <div className="placeholder-glow">
                        <span className="placeholder col-3"></span>
                      </div>
                      <div className="placeholder-glow">
                        <span className="placeholder col-4"></span>
                      </div>
                    </div>
                    <div className="app_dashboard_mid_content">
                      <div className="placeholder-glow">
                        <span className="placeholder col-2"></span>
                      </div>
                      <div className="placeholder-glow">
                        <span className="placeholder col-3"></span>
                      </div>
                    </div>
                    <div className="app_dashboard_mid_content">
                      <div className="placeholder-glow">
                        <span className="placeholder col-4"></span>
                      </div>
                      <div className="placeholder-glow">
                        <span className="placeholder col-2"></span>
                      </div>
                    </div>
                    <div className="app_dashboard_mid_content">
                      <div className="placeholder-glow">
                        <span className="placeholder col-3"></span>
                      </div>
                      <div className="placeholder-glow">
                        <span className="placeholder col-4"></span>
                      </div>
                    </div>
                  </div>
                  <div className="app_dashboard_rt mob_on_track">
                    <div className="placeholder-glow">
                      <span className="placeholder col-2"></span>
                    </div>
                    <div className="placeholder-glow">
                      <span className="placeholder col-4"></span>
                    </div>
                  </div>
                  <div className="app_dashboard_btm">
                    <div className="app_dashboard_btm_box">
                      <div className="app_dashboard_btm_box_up">
                        <div className="app_dashboard_btm_box_up_lt">
                          <div className="placeholder-glow">
                            <span
                              className="placeholder"
                              style={{
                                width: "48px",
                                height: "48px",
                                borderRadius: "4px",
                              }}
                            ></span>
                          </div>
                        </div>
                        <div className="app_dashboard_btm_box_up_rt">
                          <div className="placeholder-glow">
                            <span className="placeholder col-3"></span>
                          </div>
                          <div className="placeholder-glow">
                            <span className="placeholder col-2"></span>
                          </div>
                        </div>
                      </div>
                      <div className="app_dashboard_btm_box_dwn">
                        <div className="placeholder-glow">
                          <span className="placeholder col-6"></span>
                        </div>
                      </div>
                    </div>
                    <div className="app_dashboard_btm_box">
                      <div className="app_dashboard_btm_box_up">
                        <div className="app_dashboard_btm_box_up_lt">
                          <div className="placeholder-glow">
                            <span
                              className="placeholder"
                              style={{
                                width: "48px",
                                height: "48px",
                                borderRadius: "4px",
                              }}
                            ></span>
                          </div>
                        </div>
                        <div className="app_dashboard_btm_box_up_rt">
                          <div className="placeholder-glow">
                            <span className="placeholder col-3"></span>
                          </div>
                          <div className="placeholder-glow">
                            <span className="placeholder col-2"></span>
                          </div>
                        </div>
                      </div>
                      <div className="app_dashboard_btm_box_dwn">
                        <div className="placeholder-glow">
                          <span className="placeholder col-6"></span>
                        </div>
                      </div>
                    </div>
                    <div className="app_dashboard_btm_box">
                      <div className="app_dashboard_btm_box_up">
                        <div className="app_dashboard_btm_box_up_lt">
                          <div className="placeholder-glow">
                            <span
                              className="placeholder"
                              style={{
                                width: "48px",
                                height: "48px",
                                borderRadius: "4px",
                              }}
                            ></span>
                          </div>
                        </div>
                        <div className="app_dashboard_btm_box_up_rt">
                          <div className="placeholder-glow">
                            <span className="placeholder col-4"></span>
                          </div>
                          <div className="placeholder-glow">
                            <span className="placeholder col-2"></span>
                          </div>
                        </div>
                      </div>
                      <div className="app_dashboard_btm_box_dwn">
                        <div className="placeholder-glow">
                          <span className="placeholder col-6"></span>
                        </div>
                      </div>
                    </div>
                    <div className="app_dashboard_btm_box">
                      <div className="app_dashboard_btm_box_up">
                        <div className="app_dashboard_btm_box_up_lt">
                          <div className="placeholder-glow">
                            <span
                              className="placeholder"
                              style={{
                                width: "48px",
                                height: "48px",
                                borderRadius: "4px",
                              }}
                            ></span>
                          </div>
                        </div>
                        <div className="app_dashboard_btm_box_up_rt">
                          <div className="placeholder-glow">
                            <span className="placeholder col-3"></span>
                          </div>
                          <div className="placeholder-glow">
                            <span className="placeholder col-2"></span>
                          </div>
                        </div>
                      </div>
                      <div className="app_dashboard_btm_box_dwn">
                        <div className="placeholder-glow">
                          <span className="placeholder col-6"></span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="app_dashboard_top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', marginTop: '60px' }}>
                    <div className="app_dashboard_lt" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flex: '1', minWidth: '0' }}>
                      <div className="app_dashboard_lt_inn_img">
                        <img
                          src={appdetail?.image_url || googlelogo}
                          alt={appdetail?.name || "App Image"}
                          onError={e => {
                            e.target.src = googlelogo
                          }}
                        />
                      </div>
                      <div className="app_dashboard_lt_inn_txt" style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                        <h1 style={{ margin: '0', padding: '0' }}>{appdetail?.name || "Loading..."}</h1>
                        {lastUpdated && (
                          <p style={{
                            margin: '0',
                            fontSize: '12px',
                            color: '#888',
                            fontStyle: 'italic'
                          }}>
                            Last updated: {new Date(lastUpdated).toLocaleString()}
                          </p>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Link
                            to={`/publisher-apps/${appdetail?.item_id}`}
                            style={{
                              textDecoration: 'underline',
                              color: '#4A9EFF',
                              transition: 'color 0.3s ease',
                              pointerEvents: loading || !isAppdetailLoaded ? 'none' : 'auto',
                              opacity: loading || !isAppdetailLoaded ? 0.5 : 1
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#2E7BD6';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = '#4A9EFF';
                            }}
                          >
                            All Games from {appdetail?.publisher}
                          </Link>
                          <div
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              cursor: 'help'
                            }}
                            title="Browse all games published by this developer on KeyQuest"
                          >
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                          <a
                            href={appdetail?.platform_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              textDecoration: 'underline',
                              color: '#4A9EFF',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              transition: 'color 0.3s ease',
                              pointerEvents: loading || !isAppdetailLoaded ? 'none' : 'auto',
                              opacity: loading || !isAppdetailLoaded ? 0.5 : 1
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#2E7BD6';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = '#4A9EFF';
                            }}
                          >
                            View in
                            {/* Dynamic platform icon */}
                            <PlatformIcon 
                              platform={appdetail?.platform} 
                              style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                            />
                          </a>
                          <div
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              cursor: 'help'
                            }}
                            title="View this app directly on its official Meta Store page."
                          >
                          </div>
                        </div>

                        {/* Tracking Information Section removed per request */}
                      </div>
                    </div>
                    <div className="app_dashboard_rt desk_on_track" style={{ flexShrink: 0, maxWidth: '300px', marginTop: '60px' }}>
                      <AppsTrackingPlanModal
                        show={showPlanModal}
                        handleClose={() => setShowPlanModal(false)}
                        title=" Upgrade your plan to unlock this feature"
                      />

                      {/* Track First Modal */}
                      <Modal
                        isOpen={showTrackFirstModal}
                        toggle={() => setShowTrackFirstModal(false)}
                        centered
                      >
                        <ModalHeader toggle={() => setShowTrackFirstModal(false)}>
                          Track App First
                        </ModalHeader>
                        <ModalBody>
                          <p>You need to track this app first before accessing this feature.</p>
                        </ModalBody>
                        <ModalFooter>
                          <Button color="secondary" onClick={() => setShowTrackFirstModal(false)}>
                            Close
                          </Button>
                        </ModalFooter>
                      </Modal>

                      {/* Untrack modal removed per new requirement */}

                      {/* Screenshot Lightbox Modal */}
                      <Modal
                        isOpen={showLightbox}
                        toggle={closeLightbox}
                        centered
                        size="lg"
                        className="lightbox-modal"
                        style={{ maxWidth: '90vw' }}
                      >
                        <div className="modal-header" style={{ backgroundColor: '#1a1a1a', border: 'none' }}>
                          <h5 className="modal-title" style={{ color: '#fff' }}>
                            Screenshot {lightboxIndex + 1} of {appdetail?.screenshots?.length || 0}
                          </h5>
                          <button
                            type="button"
                            className="btn-close"
                            onClick={closeLightbox}
                            aria-label="Close"
                            style={{
                              filter: 'invert(1)', // make the default SVG white
                              opacity: 0.85
                            }}
                          ></button>
                        </div>
                        <ModalBody style={{ backgroundColor: '#1a1a1a', padding: '0', position: 'relative' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '400px',
                            maxHeight: '80vh',
                            overflow: 'hidden'
                          }}>
                            {/* Previous Button */}
                            {lightboxIndex > 0 && (
                              <button
                                onClick={prevImage}
                                style={{
                                  position: 'absolute',
                                  left: '10px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  background: 'rgba(0,0,0,0.7)',
                                  border: 'none',
                                  color: 'white',
                                  fontSize: '24px',
                                  width: '50px',
                                  height: '50px',
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  zIndex: 10,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                ‹
                              </button>
                            )}

                            {/* Main Image */}
                            <img
                              src={lightboxImage}
                              alt={`Screenshot ${lightboxIndex + 1}`}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                display: 'block'
                              }}
                            />

                            {/* Next Button */}
                            {appdetail?.screenshots && lightboxIndex < appdetail.screenshots.length - 1 && (
                              <button
                                onClick={nextImage}
                                style={{
                                  position: 'absolute',
                                  right: '10px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  background: 'rgba(0,0,0,0.7)',
                                  border: 'none',
                                  color: 'white',
                                  fontSize: '24px',
                                  width: '50px',
                                  height: '50px',
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  zIndex: 10,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                ›
                              </button>
                            )}
                          </div>
                        </ModalBody>
                      </Modal>
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.75)', margin: '8px 0' }}>
                    <p style={{ margin: 0 }}>Tracked On: {appdetail?.created_at ? formatDateToDisplay(appdetail.created_at) : "N/A"}</p>
                    <p style={{ margin: 0 }}>Tracked by 25% of KeyQuest users</p>
                  </div>
                  <div className="app_dashboard_mid" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                    <span style={{ color: 'rgba(232, 232, 232, 1)', fontSize: '14px' }}>
                      Categories: <strong>{getGenreDisplay(appdetail?.genre)}</strong>
                    </span>
                    <span style={{ color: 'rgba(232, 232, 232, 1)', fontSize: '14px' }}>
                      Price: <strong>{appdetail?.price?.amount === 0 || !appdetail?.price?.amount ? "Free" : `$${appdetail.price.amount}`}</strong>
                    </span>
                    <span style={{ color: 'rgba(232, 232, 232, 1)', fontSize: '14px' }}>
                      Content Rating: <strong>{appdetail?.age_rating || "N/A"}</strong>
                    </span>
                    <span style={{ color: 'rgba(232, 232, 232, 1)', fontSize: '14px' }}>
                      Platform: <strong>{getPlatformDisplayName(appdetail?.platform)}</strong>
                    </span>
                  </div>
                  <div className="app_dashboard_rt mob_on_track" style={{ marginTop: '60px' }}>
                    {!itemIds.includes(appdetail?.item_id) && (
                      <button
                        type="button"
                        value="Track"
                        disabled={loading || btnLoader || !isAppdetailLoaded}
                        onClick={() => {
                          setBtnLoader(true)
                          trackNewApp(appdetail?.item_id)
                        }}
                        style={{
                          padding: goBackButtonStyle.padding,
                          borderRadius: goBackButtonStyle.borderRadius,
                          cursor: goBackButtonStyle.cursor,
                          fontWeight: goBackButtonStyle.fontWeight,
                          fontSize: goBackButtonStyle.fontSize,
                          display: goBackButtonStyle.display,
                          alignItems: goBackButtonStyle.alignItems,
                          justifyContent: "center",
                          gap: goBackButtonStyle.gap,
                        }}
                      >
                        {btnLoader ? "Loading..." : "Track"}
                      </button>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px', width: '100%' }}>
                      <button
                        type="button"
                        disabled={loading || btnLoader || !isAppdetailLoaded}
                        onClick={handleKeywordTracking}
                        style={{
                          ...trackingActionButtonStyle,
                          width: '100%',
                          minWidth: 'auto',
                          marginLeft: 0,
                          opacity: loading || btnLoader || !isAppdetailLoaded ? 0.5 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (!loading && !btnLoader && isAppdetailLoaded) {
                            const btn = e.currentTarget
                            btn.style.backgroundColor = '#3a3a48'
                            btn.style.borderColor = '#505062'
                            btn.style.transform = 'translateY(-1px)'
                          }
                        }}
                        onMouseLeave={(e) => {
                          const btn = e.currentTarget
                          btn.style.backgroundColor = trackingActionButtonStyle.backgroundColor
                          btn.style.borderColor = trackingActionButtonStyle.border.split(' ')[2]
                          btn.style.color = trackingActionButtonStyle.color
                          btn.style.transform = 'translateY(0)'
                        }}
                      >
                        🔖 {itemIds.includes(appdetail?.item_id) ? 'Show Keywords for this App' : 'Track Keywords for this App'}
                      </button>
                      <button
                        type="button"
                        disabled={loading || btnLoader || !isAppdetailLoaded}
                        onClick={handleCategoryTracking}
                        style={{
                          ...trackingActionButtonStyle,
                          width: '100%',
                          minWidth: 'auto',
                          opacity: loading || btnLoader || !isAppdetailLoaded ? 0.5 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (!loading && !btnLoader && isAppdetailLoaded) {
                            const btn = e.currentTarget
                            btn.style.backgroundColor = '#3a3a48'
                            btn.style.borderColor = '#505062'
                            btn.style.transform = 'translateY(-1px)'
                          }
                        }}
                        onMouseLeave={(e) => {
                          const btn = e.currentTarget
                          btn.style.backgroundColor = trackingActionButtonStyle.backgroundColor
                          btn.style.borderColor = trackingActionButtonStyle.border.split(' ')[2]
                          btn.style.color = trackingActionButtonStyle.color
                          btn.style.transform = 'translateY(0)'
                        }}
                      >
                        🏷️ {itemIds.includes(appdetail?.item_id) ? 'Show Categories for this App' : 'Track Categories for this App'}
                      </button>
                    </div>

                    {/* Tracking Information Section (mobile) removed per request */}
                  </div>
                  <div className="app_dashboard_btm">
                    <div className="app_dashboard_btm_box" style={{ width: '265.98px', height: 'auto', minHeight: '82px' }}>
                      <div className="app_dashboard_btm_box_up">
                        <div className="app_dashboard_btm_box_up_lt">
                          <img src={dwnload} alt="" />
                        </div>
                        <div className="app_dashboard_btm_box_up_rt">
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', marginTop: '-4px' }}>
                            <p style={{ textAlign: 'right', margin: 0 }}>
                              Downloads
                            </p>
                            {(() => {
                              const aiDownloadEstimation = resolveMetricRaw('ai_download_estimation') || appdetail?.ai_download_estimation;
                              const isLoggedIn = localStorage.getItem("authUser") || localStorage.getItem("authToken")
                              if (aiDownloadEstimation && isLoggedIn) {
                                return (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const downloadData = aiDownloadEstimation;
                                      if (downloadData) {
                                        const content = {
                                          type: 'downloads-estimation',
                                          sourceType: downloadData.source_type?.type,
                                          sourceTitle: downloadData.source_type?.title,
                                          sourceDescription: downloadData.source_type?.description,
                                          confidenceScore: downloadData.confidence_score,
                                          confidenceLabel: downloadData.confidence_label,
                                          estimatedRange: downloadData.estimated_range,
                                          evidenceFound: downloadData.evidence_found || [],
                                          whyThisEstimate: downloadData.why_this_estimate || '',
                                          dataMaturityNote: downloadData.data_maturity_note || '',
                                          lastUpdated: downloadData.updated_at || new Date().toISOString()
                                        };
                                        setTooltipModalContent(content);
                                        setTooltipModalOpen(true);
                                      }
                                    }}
                                    style={{
                                      padding: '2px 8px',
                                      backgroundColor: '#F6255A',
                                      color: '#fff',
                                      border: 'none',
                                      borderRadius: '3px',
                                      cursor: 'pointer',
                                      fontSize: '9px',
                                      fontWeight: '500',
                                      whiteSpace: 'nowrap',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = '#FF6680'
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = '#F6255A'
                                    }}
                                  >
                                    See More
                                  </button>
                                )
                              }
                              return null
                            })()}
                          </div>
                          {
                            (() => {
                              const aiDownloadEstimation = resolveMetricRaw('ai_download_estimation') || appdetail?.ai_download_estimation;
                              const aiRaw = aiDownloadEstimation?.ai_downloads;
                              const fallbackRaw = resolveMetricRaw('downloads');
                              const raw = aiRaw != null ? aiRaw : fallbackRaw;
                              // A logged-out/guest visitor always sees this as locked
                              // (regardless of the raw value) — that business rule is
                              // unchanged. What changed: clicking it now opens the
                              // same shared upgrade modal for everyone instead of a
                              // plain "Register to see" link straight to /login.
                              const isLoggedIn = localStorage.getItem("authUser") || localStorage.getItem("authToken")
                              const downloadsLocked = raw === LOCK_PLACEHOLDER || (typeof raw === 'string' && raw.includes(LOCK_PLACEHOLDER)) || !isLoggedIn
                              const estimatedRange = aiDownloadEstimation?.estimated_range;

                              // Show range if available, otherwise show single value
                              const displayRange = estimatedRange && !downloadsLocked
                                ? (estimatedRange.min === 0 && estimatedRange.max === 0
                                    ? getDownloadsDisplay(0)
                                    : `${getDownloadsDisplay(estimatedRange.min)} - ${getDownloadsDisplay(estimatedRange.max)}`)
                                : (downloadsLocked ? getDownloadsDisplay(0) : (raw != null ? getDownloadsDisplay(raw) : "N/A"))

                              return (
                                <div
                                  className={`app_dashboard_value ${downloadsLocked ? 'locked' : ''}`}
                                  {...(downloadsLocked ? { role: 'button', tabIndex: 0 } : {})}
                                  onClick={(e) => { if (downloadsLocked && !loading && isAppdetailLoaded) { e.stopPropagation(); setShowPlanModal(true); } }}
                                  onKeyDown={(e) => { if (downloadsLocked && !loading && isAppdetailLoaded && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); e.stopPropagation(); setShowPlanModal(true); } }}
                                >
                                  {!downloadsLocked ? (
                                    <div className="app_dashboard_value_display" style={{ fontSize: '14px' }}>{displayRange}</div>
                                  ) : (
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      onClick={(e) => { e.stopPropagation(); setShowPlanModal(true); }}
                                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setShowPlanModal(true); } }}
                                      title="Unlock detailed analytics powered by KeyQuest's proprietary data models."
                                      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                                    >
                                      <span style={{ filter: 'blur(4px)', WebkitFilter: 'blur(4px)', opacity: 0.6, userSelect: 'none', pointerEvents: 'none' }}>{displayRange}</span>
                                      <i className="fas fa-lock" aria-hidden="true" style={{ fontSize: 12, color: '#fff', cursor: 'pointer' }} />
                                    </span>
                                  )}
                                </div>
                              )
                            })()
                          }
                        </div>
                      </div>
                      {/* Confidence bar section */}
                      {(() => {
                        const aiDownloadEstimation = resolveMetricRaw('ai_download_estimation') || appdetail?.ai_download_estimation;
                        const confidenceScore = aiDownloadEstimation?.confidence_score;
                        const confidenceLabel = aiDownloadEstimation?.confidence_label;
                        const aiRaw = aiDownloadEstimation?.ai_downloads;
                        const fallbackRaw = resolveMetricRaw('downloads');
                        const raw = aiRaw != null ? aiRaw : fallbackRaw;
                        const isLoggedIn = localStorage.getItem("authUser") || localStorage.getItem("authToken")
                        const downloadsLocked = raw === LOCK_PLACEHOLDER || (typeof raw === 'string' && raw.includes(LOCK_PLACEHOLDER)) || !isLoggedIn
                        const dataMaturityNote = formatDataMaturityNote(aiDownloadEstimation?.data_maturity_note);
                        if (confidenceScore != null && !downloadsLocked) {
                          const confidenceColor = confidenceScore >= 80 ? '#28a745' : confidenceScore >= 50 ? '#ffc107' : '#dc3545';
                          return (
                            <div style={{ marginTop: '8px' }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: '4px'
                              }}>
                                <span style={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.6)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                  Confidence Score
                                  {dataMaturityNote && (
                                    <span
                                      style={{ display: 'inline-flex', alignItems: 'center', position: 'relative', cursor: 'pointer' }}
                                      onMouseEnter={() => setActiveMaturityTooltip('downloads')}
                                      onMouseLeave={() => setActiveMaturityTooltip(null)}
                                    >
                                      <FaInfoCircle size={9} style={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                                      {activeMaturityTooltip === 'downloads' && (
                                        <div
                                          style={{
                                            position: 'absolute',
                                            bottom: 'calc(100% + 8px)',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: '240px',
                                            backgroundColor: 'rgb(23, 24, 33)',
                                            border: '1px solid rgba(255, 255, 255, 0.15)',
                                            borderRadius: '8px',
                                            padding: '10px 12px',
                                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
                                            zIndex: 1000,
                                            whiteSpace: 'normal',
                                            textAlign: 'left',
                                            cursor: 'default'
                                          }}
                                        >
                                          <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.9)', lineHeight: '1.5', fontWeight: '400' }}>
                                            {dataMaturityNote}
                                          </div>
                                        </div>
                                      )}
                                    </span>
                                  )}
                                </span>
                                {confidenceLabel && (
                                  <span style={{ fontSize: '9px', fontWeight: '600', color: confidenceColor, whiteSpace: 'nowrap' }}>
                                    {confidenceLabel.toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{
                                  flex: 1,
                                  height: '4px',
                                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                  borderRadius: '2px',
                                  overflow: 'hidden'
                                }}>
                                  <div style={{
                                    width: `${confidenceScore}%`,
                                    height: '100%',
                                    backgroundColor: confidenceColor,
                                    borderRadius: '2px',
                                    transition: 'width 0.3s ease'
                                  }} />
                                </div>
                                <span style={{
                                  fontSize: '9px',
                                  fontWeight: '600',
                                  color: 'rgba(255, 255, 255, 0.7)',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {confidenceScore}%
                                </span>
                              </div>
                            </div>
                          )
                        }
                        return null
                      })()}
                      {/* Change value section - Only show for tracked apps with an actual change value */}
                      {(() => {
                        if (!itemIds.includes(appdetail?.item_id)) return null
                        const changeValue = resolveMetricRaw('downloads_change') || appdetail?.analytics?.downloads_change
                        if (!changeValue || changeValue === LOCK_PLACEHOLDER) return null
                        const isPositive = parseFloat(changeValue) > 0
                        return (
                          <div className="app_dashboard_btm_box_dwn">
                            <div style={{
                              color: isPositive ? '#28a745' : '#dc3545',
                              fontSize: '12px',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span>{isPositive ? '↗' : '↘'}</span>
                              {isPositive ? '+' : ''}{changeValue}%
                            </div>
                          </div>
                        )
                      })()}
                      </div>
                    <div className="app_dashboard_btm_box" style={{ width: '265.98px', height: 'auto', minHeight: '82px' }}>
                      <div className="app_dashboard_btm_box_up">
                        <div className="app_dashboard_btm_box_up_lt">
                          <img src={growth} alt="" />
                        </div>
                        <div className="app_dashboard_btm_box_up_rt">
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', marginTop: '-4px' }}>
                            <p style={{ textAlign: 'right', margin: 0 }}>
                              Revenue
                            </p>
                            {(() => {
                              const aiRevenueEstimation = resolveMetricRaw('ai_revenue_estimation') || appdetail?.ai_revenue_estimation;
                              const isLoggedIn = localStorage.getItem("authUser") || localStorage.getItem("authToken")
                              if (aiRevenueEstimation && isLoggedIn) {
                                return (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const revenueData = aiRevenueEstimation;
                                      if (revenueData) {
                                        const content = {
                                          type: 'revenue-estimation',
                                          sourceType: revenueData.source_type?.type,
                                          sourceTitle: revenueData.source_type?.title,
                                          sourceDescription: revenueData.source_type?.description,
                                          confidenceScore: revenueData.confidence_score,
                                          confidenceLabel: revenueData.confidence_label,
                                          estimatedRange: revenueData.estimated_range,
                                          evidenceFound: revenueData.evidence_found || [],
                                          whyThisEstimate: revenueData.why_this_estimate || '',
                                          dataMaturityNote: revenueData.data_maturity_note || '',
                                          lastUpdated: revenueData.updated_at || new Date().toISOString()
                                        };
                                        setTooltipModalContent(content);
                                        setTooltipModalOpen(true);
                                      }
                                    }}
                                    style={{
                                      padding: '2px 8px',
                                      backgroundColor: '#F6255A',
                                      color: '#fff',
                                      border: 'none',
                                      borderRadius: '3px',
                                      cursor: 'pointer',
                                      fontSize: '9px',
                                      fontWeight: '500',
                                      whiteSpace: 'nowrap',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = '#FF6680'
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = '#F6255A'
                                    }}
                                  >
                                    See More
                                  </button>
                                )
                              }
                              return null
                            })()}
                          </div>
                          {
                            (() => {
                              const aiRevenueEstimation = resolveMetricRaw('ai_revenue_estimation') || appdetail?.ai_revenue_estimation;
                              const aiRaw = aiRevenueEstimation?.ai_revenue;
                              const fallbackRaw = resolveMetricRaw('revenue');
                              const raw = aiRaw != null ? aiRaw : fallbackRaw;
                              // A logged-out/guest visitor always sees this as locked
                              // (regardless of the raw value) — that business rule is
                              // unchanged. What changed: clicking it now opens the
                              // same shared upgrade modal for everyone instead of a
                              // plain "Register to see" link straight to /login.
                              const isLoggedIn = localStorage.getItem("authUser") || localStorage.getItem("authToken")
                              const revenueLocked = raw === LOCK_PLACEHOLDER || (typeof raw === 'string' && raw.includes(LOCK_PLACEHOLDER)) || !isLoggedIn
                              const estimatedRange = aiRevenueEstimation?.estimated_range;

                              // Show range if available, otherwise show single value
                              const displayRange = estimatedRange && !revenueLocked
                                ? (estimatedRange.min === 0 && estimatedRange.max === 0
                                    ? getRevenueDisplay(0)
                                    : `${getRevenueDisplay(estimatedRange.min)} - ${getRevenueDisplay(estimatedRange.max)}`)
                                : (revenueLocked ? getRevenueDisplay(0) : (raw != null ? getRevenueDisplay(raw) : "N/A"))

                              return (
                                <div
                                  className={`app_dashboard_value ${revenueLocked ? 'locked' : ''}`}
                                  {...(revenueLocked ? { role: 'button', tabIndex: 0 } : {})}
                                  onClick={(e) => { if (revenueLocked && !loading && isAppdetailLoaded) { e.stopPropagation(); setShowPlanModal(true); } }}
                                  onKeyDown={(e) => { if (revenueLocked && !loading && isAppdetailLoaded && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); e.stopPropagation(); setShowPlanModal(true); } }}
                                >
                                  {!revenueLocked ? (
                                    <div className="app_dashboard_value_display" style={{ fontSize: '14px' }}>{displayRange}</div>
                                  ) : (
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      onClick={(e) => { e.stopPropagation(); setShowPlanModal(true); }}
                                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setShowPlanModal(true); } }}
                                      title="Unlock detailed analytics powered by KeyQuest's proprietary data models."
                                      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                                    >
                                      <span style={{ filter: 'blur(4px)', WebkitFilter: 'blur(4px)', opacity: 0.6, userSelect: 'none', pointerEvents: 'none' }}>{displayRange}</span>
                                      <i className="fas fa-lock" aria-hidden="true" style={{ fontSize: 12, color: '#fff', cursor: 'pointer' }} />
                                    </span>
                                  )}
                                </div>
                              )
                            })()
                          }
                        </div>
                      </div>
                      {/* Confidence bar section */}
                      {(() => {
                        const aiRevenueEstimation = resolveMetricRaw('ai_revenue_estimation') || appdetail?.ai_revenue_estimation;
                        const confidenceScore = aiRevenueEstimation?.confidence_score;
                        const confidenceLabel = aiRevenueEstimation?.confidence_label;
                        const aiRaw = aiRevenueEstimation?.ai_revenue;
                        const fallbackRaw = resolveMetricRaw('revenue');
                        const raw = aiRaw != null ? aiRaw : fallbackRaw;
                        const isLoggedIn = localStorage.getItem("authUser") || localStorage.getItem("authToken")
                        const revenueLocked = raw === LOCK_PLACEHOLDER || (typeof raw === 'string' && raw.includes(LOCK_PLACEHOLDER)) || !isLoggedIn
                        const dataMaturityNote = formatDataMaturityNote(aiRevenueEstimation?.data_maturity_note);
                        if (confidenceScore != null && !revenueLocked) {
                          const confidenceColor = confidenceScore >= 80 ? '#28a745' : confidenceScore >= 50 ? '#ffc107' : '#dc3545';
                          return (
                            <div style={{ marginTop: '8px' }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: '4px'
                              }}>
                                <span style={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.6)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                  Confidence Score
                                  {dataMaturityNote && (
                                    <span
                                      style={{ display: 'inline-flex', alignItems: 'center', position: 'relative', cursor: 'pointer' }}
                                      onMouseEnter={() => setActiveMaturityTooltip('revenue')}
                                      onMouseLeave={() => setActiveMaturityTooltip(null)}
                                    >
                                      <FaInfoCircle size={9} style={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                                      {activeMaturityTooltip === 'revenue' && (
                                        <div
                                          style={{
                                            position: 'absolute',
                                            bottom: 'calc(100% + 8px)',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: '240px',
                                            backgroundColor: 'rgb(23, 24, 33)',
                                            border: '1px solid rgba(255, 255, 255, 0.15)',
                                            borderRadius: '8px',
                                            padding: '10px 12px',
                                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
                                            zIndex: 1000,
                                            whiteSpace: 'normal',
                                            textAlign: 'left',
                                            cursor: 'default'
                                          }}
                                        >
                                          <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.9)', lineHeight: '1.5', fontWeight: '400' }}>
                                            {dataMaturityNote}
                                          </div>
                                        </div>
                                      )}
                                    </span>
                                  )}
                                </span>
                                {confidenceLabel && (
                                  <span style={{ fontSize: '9px', fontWeight: '600', color: confidenceColor, whiteSpace: 'nowrap' }}>
                                    {confidenceLabel.toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{
                                  flex: 1,
                                  height: '4px',
                                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                  borderRadius: '2px',
                                  overflow: 'hidden'
                                }}>
                                  <div style={{
                                    width: `${confidenceScore}%`,
                                    height: '100%',
                                    backgroundColor: confidenceColor,
                                    borderRadius: '2px',
                                    transition: 'width 0.3s ease'
                                  }} />
                                </div>
                                <span style={{
                                  fontSize: '9px',
                                  fontWeight: '600',
                                  color: 'rgba(255, 255, 255, 0.7)',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {confidenceScore}%
                                </span>
                              </div>
                            </div>
                          )
                        }
                        return null
                      })()}
                      {/* Change value section - Only show for tracked apps with an actual change value */}
                      {(() => {
                        if (!itemIds.includes(appdetail?.item_id)) return null
                        const changeValue = resolveMetricRaw('revenue_change') || appdetail?.analytics?.revenue_change
                        if (!changeValue || changeValue === LOCK_PLACEHOLDER) return null
                        const isPositive = parseFloat(changeValue) > 0
                        return (
                          <div className="app_dashboard_btm_box_dwn">
                            <div style={{
                              color: isPositive ? '#28a745' : '#dc3545',
                              fontSize: '12px',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span>{isPositive ? '↗' : '↘'}</span>
                              {isPositive ? '+' : ''}{changeValue}%
                            </div>
                          </div>
                        )
                      })()}
                      </div>
                    <div className="app_dashboard_btm_box" style={{ width: '265.98px', height: 'auto', minHeight: '82px' }}>
                      <div className="app_dashboard_btm_box_up">
                        <div className="app_dashboard_btm_box_up_lt">
                          <img src={solar} alt="" />
                        </div>
                        <div className="app_dashboard_btm_box_up_rt">
                          <p>App Store Score</p>
                          {
                            (() => {
                              const raw = resolveMetricRaw('app_score') || appdetail?.analytics?.asr
                              const appScoreLocked = raw === LOCK_PLACEHOLDER || (typeof raw === 'string' && raw.includes(LOCK_PLACEHOLDER))
                              const display = appScoreLocked ? "0" : (raw ? formatNumber(Math.round(raw)) : "N/A")
                              return (
                                <div
                                  className={`app_dashboard_value ${appScoreLocked ? 'locked' : ''}`}
                                  {...(appScoreLocked ? { role: 'button', tabIndex: 0 } : {})}
                                  onClick={(e) => { if (appScoreLocked && !loading && isAppdetailLoaded) { e.stopPropagation(); setShowPlanModal(true); } }}
                                  onKeyDown={(e) => { if (appScoreLocked && !loading && isAppdetailLoaded && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); e.stopPropagation(); setShowPlanModal(true); } }}
                                >
                                  {!appScoreLocked ? (
                                    <div className="app_dashboard_value_display">{display}</div>
                                  ) : (
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      onClick={(e) => { e.stopPropagation(); setShowPlanModal(true); }}
                                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setShowPlanModal(true); } }}
                                      title={"Unlock detailed analytics powered by KeyQuest's proprietary data models."}
                                      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                                    >
                                      <span style={{ filter: 'blur(3px)', WebkitFilter: 'blur(3px)', userSelect: 'none' }}>{display}</span>
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                        <path d="M12 17a2 2 0 100-4 2 2 0 000 4z" fill="#FFFFFF" />
                                        <path d="M17 8V7a5 5 0 10-10 0v1H5v11h14V8h-2zm-8-1a4 4 0 118 0v1H9V7z" fill="#FFFFFF" />
                                      </svg>
                                    </span>
                                  )}
                                </div>
                              )
                            })()
                          }
                        </div>
                      </div>
                      {/* Change value section - Only show for tracked apps with an actual change value */}
                      {(() => {
                        if (!itemIds.includes(appdetail?.item_id)) return null
                        const changeValue = resolveMetricRaw('app_score_change') || appdetail?.analytics?.app_score_change
                        if (!changeValue || changeValue === LOCK_PLACEHOLDER) return null
                        const isPositive = parseFloat(changeValue) > 0
                        return (
                          <div className="app_dashboard_btm_box_dwn">
                            <div style={{
                              color: isPositive ? '#28a745' : '#dc3545',
                              fontSize: '12px',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span>{isPositive ? '↗' : '↘'}</span>
                              {isPositive ? '+' : ''}{changeValue}%
                            </div>
                          </div>
                        )
                      })()}
                    </div>
                    <div className="app_dashboard_btm_box" style={{ width: '265.98px', height: 'auto', minHeight: '82px' }}>
                      <div className="app_dashboard_btm_box_up">
                        <div className="app_dashboard_btm_box_up_lt">
                          <img src={mans} alt="" />
                        </div>
                        <div className="app_dashboard_btm_box_up_rt">
                          <p>Ratings</p>
                          <div className="app_dashboard_value_display">{appdetail?.rating?.average ? appdetail.rating.average.toFixed(1) : "—"}</div>
                        </div>
                      </div>
                      {/* Change value section - Only show for tracked apps with an actual change value */}
                      {(() => {
                        if (!itemIds.includes(appdetail?.item_id)) return null
                        const changeValue = resolveMetricRaw('ratings_change') || appdetail?.analytics?.ratings_change
                        if (!changeValue || changeValue === LOCK_PLACEHOLDER) return null
                        const isPositive = parseFloat(changeValue) > 0
                        return (
                          <div className="app_dashboard_btm_box_dwn">
                            <div style={{
                              color: isPositive ? '#28a745' : '#dc3545',
                              fontSize: '12px',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span>{isPositive ? '↗' : '↘'}</span>
                              {isPositive ? '+' : ''}{changeValue}%
                            </div>
                          </div>
                        )
                      })()}
                    </div>
                  </div>
                </>
              )}

              {isAppdetailLoaded && appdetail?.name && (
                <div className="app_dashboard_overview" style={{ margin: '24px 0' }}>
                  <h2 style={{ fontSize: '22px', fontWeight: 600, color: '#fff', marginBottom: '8px' }}>
                    Overview
                  </h2>
                  <p style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)', maxWidth: 'min(1600px, 100%)', lineHeight: 1.6 }}>
                    {seoOverviewText}
                  </p>
                </div>
              )}

              <div
                className="app_dashboard_tools_strip mb-3"
                style={{
                  background: '#171821',
                  border: '1px solid #353748',
                  borderRadius: '10px',
                  padding: '12px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: '8px',
                }}
              >
                  {/* AI tool cards — visually distinct from the tabs below (badge + card, not a pill row). */}
                  {/* CSS grid keeps every card the same size (equal columns) and reflows to fewer
                      columns per row on narrow screens instead of overflowing or shrinking unevenly. */}
                  {[
                    {
                      key: 'ai-reports',
                      label: 'AI Reports',
                      Icon: FaRobot,
                      onActivate: () => {
                        console.log('AI Report button clicked. gameID:', gameID, 'Cache available:', !!appInfoCache[gameID])
                        setShowAIReportSeparately(true)
                        if (appInfoCache[gameID]) {
                          setAppInfo(appInfoCache[gameID])
                        }
                      },
                    },
                    {
                      key: 'forecasting',
                      label: 'Forecasting',
                      Icon: FaSearch,
                      onActivate: () => setShowForecastingSeparately(true),
                    },
                    {
                      key: 'ai-cmo',
                      label: 'AI CMO',
                      Icon: FaChartLine,
                      onActivate: () => setShowAICMOSeparately(true),
                      // AI CMO runs its own scan/tracking independent of this app's
                      // App Tracking status, so it shouldn't be gated behind "track
                      // this app first" like the other tools here.
                      alwaysUnlocked: true,
                    },
                    {
                      key: 'social-comparison',
                      label: 'Social Comparison',
                      Icon: FaChartPie,
                      onActivate: () => setShowSocialComparisonSeparately(true),
                      // Same as AI CMO above — tracked separately from App Tracking.
                      alwaysUnlocked: true,
                    },
                    {
                      key: 'ugc-explorer',
                      label: 'UGC',
                      Icon: FaVideo,
                      onActivate: () => navigate(
                        appdetail?.item_id ? `/ugc-explorer/youtube/${appdetail.item_id}` : '/ugc-explorer'
                      ),
                      // UGC coverage can be explored for any app, tracked or
                      // not - same as AI CMO/Social Comparison above.
                      alwaysUnlocked: true,
                    },
                    {
                      key: 'ad-discovery',
                      label: 'Ad Discovery',
                      Icon: FaTrophy,
                      // Opens Ad Discovery already searching this app (see the
                      // deep-link handling in CreativeInsightsDashboard).
                      onActivate: () => {
                        if (!appdetail?.name) return navigate('/creative-insights')
                        const params = new URLSearchParams({ app_name: appdetail.name })
                        if (appdetail.item_id) params.set('app_id', String(appdetail.item_id))
                        navigate(`/creative-insights?${params.toString()}`)
                      },
                      // Same as UGC above - Ad Discovery doesn't require
                      // this app to be tracked first.
                      alwaysUnlocked: true,
                      // Ad Discovery only has ad data for Meta today (its own
                      // platform tabs disable Steam/Apple Vision/Pico), so apps
                      // from those stores get the same "Coming soon" state.
                      comingSoon:
                        !!appdetail?.platform &&
                        !PlatformsApi.getPlatformAliases('meta').includes(appdetail.platform.toLowerCase().trim()),
                    },
                  ].map(tool => {
                    const isTracked = itemIds.includes(appdetail?.item_id);
                    const isLocked = !tool.alwaysUnlocked && !loading && isAppdetailLoaded && !isTracked;
                    const isComingSoon = Boolean(tool.comingSoon);
                    // Locked and coming-soon tools share the same dimmed look.
                    const isDimmed = isLocked || isComingSoon;
                    const { Icon } = tool;
                    return (
                      <button
                        key={tool.key}
                        disabled={loading || !isAppdetailLoaded}
                        aria-disabled={isComingSoon}
                        onClick={() => {
                          if (isComingSoon) return;
                          if (isLocked) {
                            const raw = appdetail?.analytics;
                            const hasLockedMetric = Object.values(raw || {}).some(v =>
                              typeof v === 'string' && v.includes('%UPGRADE_KEYQ%')
                            );
                            if (hasLockedMetric) {
                              setShowPlanModal(true);
                            } else {
                              toastr.error("Track the app first", "", {
                                positionClass: "toast-bottom-right",
                                closeButton: true,
                                progressBar: true,
                                timeOut: 3000,
                              });
                            }
                            return;
                          }
                          tool.onActivate();
                        }}
                        onMouseEnter={(e) => {
                          if (!loading && isAppdetailLoaded && !isDimmed) {
                            e.currentTarget.style.background = '#23242f'
                            e.currentTarget.style.borderColor = 'rgba(246, 37, 90, 0.7)'
                            e.currentTarget.style.transform = 'translateY(-2px)'
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(246, 37, 90, 0.2)'
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = isDimmed ? 'rgba(255,255,255,0.03)' : '#1d1e29'
                          e.currentTarget.style.borderColor = isDimmed ? 'rgba(255,255,255,0.1)' : 'rgba(246, 37, 90, 0.35)'
                          e.currentTarget.style.transform = 'translateY(0)'
                          e.currentTarget.style.boxShadow = 'none'
                        }}
                        style={{
                          padding: '8px 10px',
                          borderRadius: '8px',
                          border: isDimmed ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(246, 37, 90, 0.35)',
                          background: isDimmed ? 'rgba(255,255,255,0.03)' : '#1d1e29',
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          height: '48px',
                          boxSizing: 'border-box',
                          textAlign: 'left',
                          transition: 'background 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
                          cursor: loading || !isAppdetailLoaded || isComingSoon ? 'not-allowed' : 'pointer',
                          opacity: loading || !isAppdetailLoaded ? 0.6 : 1,
                        }}
                        title={
                          isComingSoon
                            ? `${tool.label} is coming soon for ${getPlatformDisplayName(appdetail?.platform)} apps`
                            : isLocked
                              ? `Track this app first to unlock ${tool.label}`
                              : tool.label
                        }
                      >
                        <div
                          style={{
                            width: '28px',
                            height: '28px',
                            borderRadius: '8px',
                            background: isDimmed ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg, rgba(255,154,184,0.25), rgba(246,37,90,0.25))',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            marginRight: '8px',
                          }}
                        >
                          <Icon style={{ fontSize: '13px', color: isDimmed ? 'rgba(255,255,255,0.35)' : '#F6255A' }} />
                        </div>
                        <span
                          style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: isDimmed ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.9)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            flex: '0 1 auto',
                            minWidth: 0,
                          }}
                        >
                          {tool.label}
                        </span>
                        <i
                          className="uil-angle-right"
                          style={{
                            fontSize: '13px',
                            color: isDimmed ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.4)',
                            flexShrink: 0,
                            marginLeft: 'auto',
                            paddingLeft: '8px',
                          }}
                        ></i>
                      </button>
                    );
                  })}
              </div>

              <div className="app_dashboard_tabs" style={{ overflow: 'visible' }}>
                <div className="mb-2">
                  <nav style={{ margin: 0 }}>
                    <div class="nav nav-tabs" id="nav-tab" role="tablist" style={{
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap',
                      padding: 0,
                      margin: 0,
                      borderBottom: 'none'
                    }}>
                      {tabs.map((tab, index) => {
                        const IconComponent = tab.icon
                        return (
                          <button
                            key={index}
                            disabled={loading || !isAppdetailLoaded}
                            onClick={() => setActiveTab(index)}
                            className={`nav-link ${activeTab === index ? "active" : ""}`}
                            style={{
                              opacity: loading || !isAppdetailLoaded ? 0.5 : 1,
                              cursor: loading || !isAppdetailLoaded ? 'not-allowed' : 'pointer',
                              padding: '8px 16px',
                              fontSize: '14px',
                              fontWeight: '600',
                              borderRadius: '8px',
                              border: activeTab === index ? '2px solid #f6255a' : '2px solid transparent',
                              backgroundColor: activeTab === index ? '#f6255a' : 'rgba(255, 255, 255, 0.1)',
                              color: activeTab === index ? '#ffffff' : 'rgba(255, 255, 255, 0.8)',
                              transition: 'all 0.3s ease',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              minHeight: '40px',
                              boxShadow: activeTab === index ? '0 4px 12px rgba(246, 37, 90, 0.3)' : 'none'
                            }}
                            onMouseEnter={(e) => {
                              if (!loading && isAppdetailLoaded && activeTab !== index) {
                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)'
                                e.target.style.transform = 'translateY(-2px)'
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (activeTab !== index) {
                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
                                e.target.style.transform = 'translateY(0)'
                              }
                            }}
                          >
                            {IconComponent && <IconComponent style={{ fontSize: '16px' }} />}
                            {tab.label}
                          </button>
                        )
                      })}
                    </div>
                  </nav>
                </div>
                <div class="tab-content" id="nav-tabContent" style={{ overflow: 'visible' }}>
                  {tabs[activeTab].content}
                </div>
              </div>
            </div>
          </div>

          {/* Loading Modal */}
          <TrackingDetailsPopup
            modaltoggle={modal_toggle}
            togglemodal={toggle_modal}
            metric={selectedMetric}
            value={metricValue}
          />

          {/* Export Modal */}
          <Modal isOpen={showExportModal} toggle={closeExportModal} centered>
            <div className="modal-header">
              <h5 className="modal-title">Export Data</h5>
              <button
                type="button"
                className="btn-close"
                onClick={closeExportModal}
                aria-label="Close"
              ></button>
            </div>
            <ModalBody>
              <div className="mb-2">
                <p className="mb-2">
                  Select the format to export <strong>{exportMetricType === 'analytics' ? 'Analytics Overview' :
                    exportMetricType === 'asr' ? 'App Store Score' :
                      exportMetricType === 'ktr' ? 'Keyword Relevance' :
                        exportMetricType === 'ku' ? 'Keyword Usage' :
                          exportMetricType === 'downloads' ? 'Daily Downloads' :
                            exportMetricType === 'aur' ? 'Average User Rating' : exportMetricType}</strong> data:
                </p>
              </div>

              <div className="mb-4">
                <div className="d-flex flex-column gap-3">
                  <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="exportFormat"
                      value="csv"
                      checked={selectedFormat === 'csv'}
                      onChange={(e) => setSelectedFormat(e.target.value)}
                    />
                    <div>
                      <strong>CSV</strong>
                      <div className="text-muted small">Comma-separated values file (.csv)</div>
                    </div>
                  </label>

                  <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="exportFormat"
                      value="pdf"
                      checked={selectedFormat === 'pdf'}
                      onChange={(e) => setSelectedFormat(e.target.value)}
                    />
                    <div>
                      <strong>PDF</strong>
                      <div className="text-muted small">Portable document format (.pdf)</div>
                    </div>
                  </label>

                  <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="exportFormat"
                      value="xlsx"
                      checked={selectedFormat === 'xlsx'}
                      onChange={(e) => setSelectedFormat(e.target.value)}
                    />
                    <div>
                      <strong>Excel</strong>
                      <div className="text-muted small">Excel spreadsheet (.xlsx)</div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeExportModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleExportSubmit}
                >
                  📊 Export {selectedFormat.toUpperCase()}
                </button>
              </div>
            </ModalBody>
          </Modal>

          {/* Tooltip Information Modal */}
          <Modal 
            isOpen={tooltipModalOpen} 
            toggle={() => setTooltipModalOpen(!tooltipModalOpen)} 
            centered 
            size="xl"
            contentClassName="bg-dark border-0 overflow-hidden"
            style={{ maxWidth: "900px" }}
          >
            <div style={{
              backgroundColor: "rgba(23, 24, 33, 1)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "16px",
              overflow: "hidden"
            }}>
              <div style={{ padding: "24px", borderBottom: "1px solid rgba(255, 255, 255, 0.05)" }} className="d-flex justify-content-between align-items-center">
                <div>
                  <h2 style={{ fontSize: "18px", fontWeight: "bold", color: "white", margin: 0 }}>
                    {tooltipModalContent?.type === 'downloads-estimation' ? 'Downloads Estimation' : 'Revenue Estimation'}
                  </h2>
                  <p style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.5)", margin: "2px 0 0 0" }}>How this estimate was generated</p>
                </div>
                <button
                  type="button"
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "rgba(255, 255, 255, 0.6)",
                    fontSize: "24px",
                    cursor: "pointer",
                    padding: "0"
                  }}
                  onClick={() => setTooltipModalOpen(false)}
                >
                  ×
                </button>
              </div>
              
              <div style={{ padding: "24px", maxHeight: "70vh", overflowY: "auto" }}>
                {tooltipModalContent?.type === 'revenue-estimation' || tooltipModalContent?.type === 'downloads-estimation' ? (
                  <>
                  <div className="row">
                    {/* Show Direct Data section if source_type is DIRECT */}
                    {tooltipModalContent?.sourceType === 'EVIDENCE_BASED' && (
                      <div className="col-md-12" style={{ paddingRight: "12px" }}>
                        <div style={{
                          backgroundColor: "rgba(26, 27, 38, 1)",
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                          borderRadius: "12px",
                          padding: "20px",
                          height: "100%"
                        }}>
                          <div className="d-flex align-items-center mb-4">
                            <div style={{
                              width: "48px",
                              height: "48px",
                              borderRadius: "12px",
                              backgroundColor: "rgba(16, 185, 129, 0.15)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginRight: "16px"
                            }}>
                              <Check size={24} style={{ color: "#10b981" }} />
                            </div>
                            <div>
                              <h4 style={{ fontSize: "14px", fontWeight: "600", color: "white", margin: "0 0 4px 0" }}>
                                {tooltipModalContent?.sourceTitle || 'Direct Data Estimate'}
                              </h4>
                              <p style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.5)", margin: 0 }}>
                                {tooltipModalContent?.sourceDescription || 'Generated using verified metrics and direct data sources'}
                              </p>
                            </div>
                          </div>
                          
                          <div style={{ marginBottom: "20px" }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span style={{ fontSize: "13px", fontWeight: "600", color: "white" }}>Confidence</span>
                              <span style={{
                                fontSize: "11px",
                                fontWeight: "600",
                                color: tooltipModalContent?.confidenceLabel === 'HIGH' ? "#10b981" : 
                                       tooltipModalContent?.confidenceLabel === 'MEDIUM' ? "#f59e0b" : "#ef4444",
                                backgroundColor: tooltipModalContent?.confidenceLabel === 'HIGH' ? "rgba(16, 185, 129, 0.15)" : 
                                                  tooltipModalContent?.confidenceLabel === 'MEDIUM' ? "rgba(245, 158, 11, 0.15)" : "rgba(239, 68, 68, 0.15)",
                                padding: "4px 8px",
                                borderRadius: "6px"
                              }}>
                                {tooltipModalContent?.confidenceLabel} ({tooltipModalContent?.confidenceScore}%)
                              </span>
                            </div>
                            <div style={{
                              height: "6px",
                              backgroundColor: "rgba(255, 255, 255, 0.1)",
                              borderRadius: "3px",
                              overflow: "hidden"
                            }}>
                              <div style={{
                                width: `${tooltipModalContent?.confidenceScore}%`,
                                height: "100%",
                                backgroundColor: tooltipModalContent?.confidenceLabel === 'HIGH' ? "#10b981" : 
                                                 tooltipModalContent?.confidenceLabel === 'MEDIUM' ? "#f59e0b" : "#ef4444",
                                borderRadius: "3px"
                              }}></div>
                            </div>
                            <p style={{ 
                              fontSize: "11px", 
                              color: tooltipModalContent?.confidenceLabel === 'HIGH' ? "#10b981" : 
                                     tooltipModalContent?.confidenceLabel === 'MEDIUM' ? "#f59e0b" : "#ef4444", 
                              margin: "4px 0 0 0" 
                            }}>
                              {tooltipModalContent?.confidenceLabel === 'HIGH' ? 'Very reliable estimate' : 
                               tooltipModalContent?.confidenceLabel === 'MEDIUM' ? 'Moderate reliability' : 'Low reliability'}
                            </p>
                          </div>

                          {tooltipModalContent?.estimatedRange && (
                            <div style={{ marginBottom: "20px" }}>
                              <p style={{ fontSize: "13px", fontWeight: "600", color: "white", margin: "0 0 8px 0" }}>
                                Range: {(() => {
                                  const { min, max } = tooltipModalContent.estimatedRange;
                                  if (min === 0 && max === 0) {
                                    return tooltipModalContent?.type === 'downloads-estimation' ? getDownloadsDisplay(0) : getRevenueDisplay(0);
                                  }
                                  const minDisplay = tooltipModalContent?.type === 'downloads-estimation' ? getDownloadsDisplay(min) : getRevenueDisplay(min);
                                  const maxDisplay = tooltipModalContent?.type === 'downloads-estimation' ? getDownloadsDisplay(max) : getRevenueDisplay(max);
                                  return `${minDisplay} - ${maxDisplay}`;
                                })()}
                              </p>
                            </div>
                          )}

                          <div style={{ marginBottom: "20px" }}>
                            <h4 style={{ fontSize: "13px", fontWeight: "600", color: "white", margin: "0 0 12px 0" }}>Direct Data Found</h4>
                            <div>
                              {tooltipModalContent?.evidenceFound?.map((item, index) => (
                                <div key={index} style={{ display: "flex", alignItems: "flex-start", marginBottom: "8px" }}>
                                  <Check size={16} style={{ color: "#10b981", marginRight: "12px", flexShrink: 0, marginTop: "2px" }} />
                                  <span style={{ fontSize: "13px", color: "rgba(255, 255, 255, 0.8)", lineHeight: "1.5" }}>{item}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div>
                            <h4 style={{ fontSize: "13px", fontWeight: "600", color: "white", margin: "0 0 8px 0" }}>Why this estimate?</h4>
                            <p style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.6)", lineHeight: "1.6", margin: 0 }}>
                              {tooltipModalContent?.whyThisEstimate || 'Direct data supports this estimate.'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show Agentic Model section if source_type is AGENTIC_MODEL */}
                    {(
                      tooltipModalContent?.sourceType === 'HYBRID_ESTIMATE' ||
                      tooltipModalContent?.sourceType === 'AGENTIC_MODEL'
                    ) && (
                      <div className={`${tooltipModalContent?.sourceType === 'EVIDENCE_BASED' ? 'col-md-6' : 'col-md-12'}`} style={{ 
                        paddingLeft: tooltipModalContent?.sourceType === 'EVIDENCE_BASED' ? "12px" : "0",
                        paddingRight: tooltipModalContent?.sourceType === 'EVIDENCE_BASED' ? "0" : "12px"
                      }}>
                        <div style={{
                          backgroundColor: "rgba(26, 27, 38, 1)",
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                          borderRadius: "12px",
                          padding: "20px",
                          height: "100%"
                        }}>
                          <div className="d-flex align-items-center mb-4">
                            <div style={{
                              width: "48px",
                              height: "48px",
                              borderRadius: "12px",
                              backgroundColor: "rgba(139, 92, 246, 0.15)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginRight: "16px"
                            }}>
                              <FaRobot size={24} style={{ color: "#8b5cf6" }} />
                            </div>
                            <div>
                              <h4 style={{ fontSize: "14px", fontWeight: "600", color: "white", margin: "0 0 4px 0" }}>
                                {tooltipModalContent?.sourceTitle || 'Agentic Model Estimate'}
                              </h4>
                              <p style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.5)", margin: 0 }}>
                                {tooltipModalContent?.sourceDescription || 'Generated using proprietary behavioral and category-based estimation models'}
                              </p>
                            </div>
                          </div>
                          
                          <div style={{ marginBottom: "20px" }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span style={{ fontSize: "13px", fontWeight: "600", color: "white" }}>Confidence</span>
                              <span style={{
                                fontSize: "11px",
                                fontWeight: "600",
                                color: tooltipModalContent?.confidenceLabel === 'HIGH' ? "#10b981" : 
                                       tooltipModalContent?.confidenceLabel === 'MEDIUM' ? "#f59e0b" : "#ef4444",
                                backgroundColor: tooltipModalContent?.confidenceLabel === 'HIGH' ? "rgba(16, 185, 129, 0.15)" : 
                                                  tooltipModalContent?.confidenceLabel === 'MEDIUM' ? "rgba(245, 158, 11, 0.15)" : "rgba(239, 68, 68, 0.15)",
                                padding: "4px 8px",
                                borderRadius: "6px"
                              }}>
                                {tooltipModalContent?.confidenceLabel} ({tooltipModalContent?.confidenceScore}%)
                              </span>
                            </div>
                            <div style={{
                              height: "6px",
                              backgroundColor: "rgba(255, 255, 255, 0.1)",
                              borderRadius: "3px",
                              overflow: "hidden"
                            }}>
                              <div style={{
                                width: `${tooltipModalContent?.confidenceScore}%`,
                                height: "100%",
                                backgroundColor: tooltipModalContent?.confidenceLabel === 'HIGH' ? "#10b981" : 
                                                 tooltipModalContent?.confidenceLabel === 'MEDIUM' ? "#f59e0b" : "#ef4444",
                                borderRadius: "3px"
                              }}></div>
                            </div>
                            <p style={{ 
                              fontSize: "11px", 
                              color: tooltipModalContent?.confidenceLabel === 'HIGH' ? "#10b981" : 
                                     tooltipModalContent?.confidenceLabel === 'MEDIUM' ? "#f59e0b" : "#ef4444", 
                              margin: "4px 0 0 0" 
                            }}>
                              {tooltipModalContent?.confidenceLabel === 'HIGH' ? 'Very reliable estimate' : 
                               tooltipModalContent?.confidenceLabel === 'MEDIUM' ? 'Moderate reliability' : 'Low reliability'}
                            </p>
                          </div>

                          {tooltipModalContent?.estimatedRange && (
                            <div style={{ marginBottom: "20px" }}>
                              <p style={{ fontSize: "13px", fontWeight: "600", color: "white", margin: "0 0 8px 0" }}>
                                Range: {(() => {
                                  const { min, max } = tooltipModalContent.estimatedRange;
                                  if (min === 0 && max === 0) {
                                    return tooltipModalContent?.type === 'downloads-estimation' ? getDownloadsDisplay(0) : getRevenueDisplay(0);
                                  }
                                  const minDisplay = tooltipModalContent?.type === 'downloads-estimation' ? getDownloadsDisplay(min) : getRevenueDisplay(min);
                                  const maxDisplay = tooltipModalContent?.type === 'downloads-estimation' ? getDownloadsDisplay(max) : getRevenueDisplay(max);
                                  return `${minDisplay} - ${maxDisplay}`;
                                })()}
                              </p>
                            </div>
                          )}

                          <div style={{ marginBottom: "20px" }}>
                            <h4 style={{ fontSize: "13px", fontWeight: "600", color: "white", margin: "0 0 12px 0" }}>Model Signals Used</h4>
                            <div>
                              {tooltipModalContent?.evidenceFound?.map((signal, index) => (
                                <div key={index} style={{ display: "flex", alignItems: "flex-start", marginBottom: "8px" }}>
                                  <Check size={16} style={{ color: "#f59e0b", marginRight: "12px", flexShrink: 0, marginTop: "2px" }} />
                                  <span style={{ fontSize: "13px", color: "rgba(255, 255, 255, 0.8)", lineHeight: "1.5" }}>{signal}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div>
                            <h4 style={{ fontSize: "13px", fontWeight: "600", color: "white", margin: "0 0 8px 0" }}>Why this estimate?</h4>
                            <p style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.6)", lineHeight: "1.6", margin: 0 }}>
                              {tooltipModalContent?.whyThisEstimate || 'Model-based estimation applied due to insufficient direct data.'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {formatDataMaturityNote(tooltipModalContent?.dataMaturityNote) && (
                    <div style={{
                      marginTop: "16px",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "10px",
                      backgroundColor: "rgba(245, 158, 11, 0.1)",
                      border: "1px solid rgba(245, 158, 11, 0.25)",
                      borderRadius: "10px",
                      padding: "14px 16px"
                    }}>
                      <FaInfoCircle size={14} style={{ color: "#f59e0b", marginTop: "2px", flexShrink: 0 }} />
                      <p style={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.75)", lineHeight: "1.6", margin: 0 }}>
                        {formatDataMaturityNote(tooltipModalContent?.dataMaturityNote)}
                      </p>
                    </div>
                  )}
                  </>
                ) : (
                  tooltipModalContent
                )}
              </div>
              
              <div style={{ padding: "20px 24px", borderTop: "1px solid rgba(255, 255, 255, 0.05)" }} className="d-flex justify-content-between align-items-center">
                <small style={{ fontSize: "11px", color: "rgba(255, 255, 255, 0.4)" }}>
                  Last updated: {tooltipModalContent?.lastUpdated ? 
                    new Date(tooltipModalContent.lastUpdated).toLocaleDateString('en-US', { 
                      month: '2-digit', 
                      day: '2-digit', 
                      year: 'numeric' 
                    }).replace(/\//g, '/') : 
                    '06/05/2026'
                  }
                </small>
                <button
                    type="button"
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "transparent",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      color: "rgba(255, 255, 255, 0.7)",
                      borderRadius: "8px",
                      fontSize: "13px",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "all 0.2s ease"
                    }}
                    onClick={() => setTooltipModalOpen(false)}
                  >
                    Close
                  </button>
              </div>
            </div>
          </Modal>

        </div>
      )}

      {/* Tooltip arrow styling for revenue tooltip */}
      <style>{`
        .tooltip.show[data-popper-placement^="top"] .tooltip-arrow::before {
          border-top-color: #000000 !important;
        }
        .tooltip.show[data-popper-placement^="top"] .tooltip-arrow::after {
          border-top-color: #FFFFFF !important;
          margin-top: -1px;
        }
      `}</style>

      {/* Responsive Styles for Go Back and Top Right Tracking buttons */}
      <style>{`
        @media (max-width: 768px) {
          .desk_on_track {
            display: none !important;
          }
          .go-back-btn-top-wrapper {
            top: 15px !important;
            left: 15px !important;
          }
          .top-right-tracking-buttons {
            top: 15px !important;
            right: 15px !important;
          }
          .top-right-tracking-buttons button {
            padding: 4px 8px !important;
            font-size: 0.75rem !important;
          }
          .go-back-btn-top {
            padding: 4px 8px !important;
            font-size: 0.8rem !important;
          }
        }
        @media (max-width: 480px) {
          .desk_on_track {
            display: none !important;
          }
          .go-back-btn-top-wrapper {
            top: 10px !important;
            left: 10px !important;
          }
          .top-right-tracking-buttons {
            top: 10px !important;
            right: 10px !important;
          }
          .top-right-tracking-buttons > div {
            gap: 3px !important;
          }
          .top-right-tracking-buttons button {
            padding: 3px 6px !important;
            font-size: 0.7rem !important;
          }
          .go-back-btn-top {
            padding: 3px 6px !important;
            font-size: 0.75rem !important;
          }
        }
      `}</style>

      <style>{`
        .custom-ai-datepicker-minimal {
          background: none !important;
          border: none !important;
          color: #a0a0a0 !important;
          font-size: 14px !important;
          font-weight: 600 !important;
          padding: 0 !important;
          width: 50px !important;
          cursor: pointer !important;
          outline: none !important;
          text-align: left;
          transition: all 0.2s ease;
        }
        .custom-ai-datepicker-minimal:hover {
          color: #F6255A !important;
        }
        .react-datepicker-wrapper {
          width: auto !important;
        }
        .react-datepicker__input-container input {
           color: inherit !important;
        }
        .react-datepicker-popper {
          z-index: 9999 !important;
        }
      `}</style>
    </React.Fragment>
  )
}
